import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, API, idr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import BarcodeScanner from "../components/hris/BarcodeScanner";
import { cetak } from "../components/faktur/FakturPrint";
import {
  Package, Plus, Search, Barcode, Printer, X, AlertTriangle,
  Settings2, Upload, Download, Tags, Wand2, Power, PowerOff, HelpCircle,
  TrendingDown, GitMerge, CheckCircle2, XCircle,
} from "lucide-react";
import LoadingState from "@/components/ui/loading-state";
import { TabelDesktop, DaftarKartuMobile, KartuBaris, KartuBarisHeader, KartuBarisMeta, KartuBarisAksi } from "@/components/ui/responsive-table";
import { useDebounce } from "@/hooks/useDebounce";
import { useDataQuery } from "@/hooks/useDataQuery";
import FilterProduk, { TESTID_MASTER_PRODUK, opsiDariServer } from "@/components/produk/FilterProduk";

const OPSI_KOSONG = {
  brand: [],
  // BARU (poin 1 & 6, brief 23-24 Agu 2026) — dua daftar brand terpisah
  // (Gadget vs Accessories), lihat `daftarBrandUntukKategoriUtama` di bawah.
  brand_gadget: [], brand_accessories: [],
  satuan: ["pcs"], kategori_custom: [], aging_hari: 60,
  // BARU (brief owner 31 Agu 2026, poin 1-2) — peta kode pembangun SKU +
  // metadata read-only dari backend (`master_opsi.py::get_master_opsi`).
  kode_sku: [], dimensi_kode_sku: [], pemisah_sku: "/", pemisah_nama: " / ",
  // Read-only dari backend (`master_opsi.py::get_master_opsi`) — fallback di
  // bawah (`KATEGORI_UTAMA_FALLBACK`/`VARIAN_SECOND_FALLBACK`) hanya dipakai
  // sebelum request pertama selesai, supaya form tidak kosong melompong.
  kategori_utama: [], varian_second: [],
};
// Diekspor supaya Layout.jsx bisa prefetch query yang SAMA PERSIS (key + param
// halaman pertama tanpa filter) saat kursor hover menu "Master Produk" cukup
// lama — satu-satunya sumber kebenaran untuk key ini, tidak ada duplikat literal.
export const PRODUK_QK = "inventory-products";
export const OPSI_QK = ["inventory-master-opsi"];
export const PRODUK_DEFAULT_PARAMS = { sort: "nama", arah: "asc", page: 1, per_page: 50 };

// ============ Hierarki Kategori (poin 2-4 brief bos 23 Agu 2026) ============
// Sumber kebenaran untuk isi & urutan Kategori Utama + Varian Second adalah
// backend (`GET /inventory/master-opsi` -> `kategori_utama[]`/`varian_second[]`,
// lihat `routers/inventory/master_opsi.py`) — dua konstanta di bawah HANYA
// dipakai sebagai fallback tampilan sebelum request pertama selesai (label UI
// murni, bukan aturan akun/validasi — itu tetap ditegakkan backend), supaya
// form tidak kosong saat pertama dibuka. Kalau backend sudah menjawab, nilai
// backend SELALU dipakai (lihat `kategoriUtamaList`/`varianSecondList` di
// dalam komponen).
const KATEGORI_UTAMA_FALLBACK = [
  { key: "handphone", label: "Handphone", wajib_status: true },
  { key: "tab", label: "Tab", wajib_status: true },
  { key: "laptop", label: "Laptop", wajib_status: true },
  { key: "wearable", label: "Wearable", wajib_status: true },
  { key: "original_acc", label: "Original Acc", wajib_status: false },
  { key: "accessories", label: "Accessories", wajib_status: false },
  { key: "sim_sticker", label: "SIM Sticker", wajib_status: false },
];
const VARIAN_SECOND_FALLBACK = [
  "Second Resmi", "Second Resmi Minus", "Second Unlock", "Second Unlock Minus",
  "Second Simlock", "Second Simlock Minus", "Second Minus NFS",
];

// Alias 3 kategori LAMA (sebelum migrasi hierarki 24 Agu 2026, lihat
// `routers/finance/_shared.py::ALIAS_KATEGORI`) — dipakai HANYA untuk mengisi
// form Ubah saat membuka produk lama yang field `kategori_utama`-nya belum
// pernah ditulis backend (dokumen belum pernah disentuh sejak migrasi, jadi
// belum lewat `_validate`). Bukan sumber kebenaran akun/kategori — itu tetap
// backend; ini murni derivasi tampilan supaya dropdown terisi benar.
const ALIAS_KATEGORI_LAMA = { unit_baru: "handphone_baru", unit_second: "handphone_second", aksesoris: "accessories" };

// Uraikan `kategori` mentah (kanonik atau alias lama) jadi {kategori_utama,
// status_kondisi} untuk mengisi form — kebalikan dari `komposKategori` di
// bawah. Memakai `p.kategori_utama` dari dokumen kalau ADA (field yang
// disimpan backend, otoritatif); baru jatuh ke pemotongan akhiran sendiri
// kalau dokumen belum pernah disentuh sejak migrasi.
function uraiKategori(p) {
  const raw = (p?.kategori || "").trim();
  const norm = ALIAS_KATEGORI_LAMA[raw] || raw;
  let status = "";
  let parentDariSuffix = norm;
  for (const akhiran of ["_baru", "_second"]) {
    if (norm.endsWith(akhiran)) {
      parentDariSuffix = norm.slice(0, -akhiran.length);
      status = akhiran === "_baru" ? "baru" : "second";
      break;
    }
  }
  return { kategori_utama: p?.kategori_utama || parentDariSuffix, status_kondisi: status };
}

// Gabungkan kategori_utama+status_kondisi jadi kunci akuntansi kanonik yang
// dikirim ke backend (`{parent}_baru`/`{parent}_second` untuk gadget, parent
// polos untuk non-gadget) — "" kalau gadget tapi status belum dipilih, supaya
// pemanggil bisa mendeteksi form belum lengkap tanpa menebak.
function komposKategori(kategoriUtama, statusKondisi, gadgetKeySet) {
  if (!kategoriUtama) return "";
  if (!gadgetKeySet.has(kategoriUtama)) return kategoriUtama;
  return statusKondisi ? `${kategoriUtama}_${statusKondisi}` : "";
}

// ============ Kode SKU per kolom (brief owner 31 Agu 2026, poin 1-2) ============
// Lima kolom pembangun + labelnya. Sumber kebenaran tetap backend
// (`master_opsi.py::DIMENSI_KODE_SKU`, dikirim lewat `dimensi_kode_sku`);
// konstanta ini cuma fallback tampilan sebelum request pertama selesai, pola
// SAMA dengan KATEGORI_UTAMA_FALLBACK di atas.
const DIMENSI_KODE_SKU_FALLBACK = [
  { key: "kategori", label: "Kategori Utama" },
  { key: "brand", label: "Brand" },
  { key: "tipe", label: "Type" },
  { key: "ram_storage", label: "Ram/Storage" },
  { key: "warna", label: "Warna" },
  { key: "distributor", label: "Distributor" },
];
// P1/P2 — SATU daftar enam sub-kolom, dipakai bersama oleh form, pratinjau,
// dan payload. Urutannya ADALAH urutan segmen SKU (cermin
// `products.py::DIMENSI_BAHAN`).
const DIMENSI_BAHAN = [
  { key: "kategori", label: "Kategori", pilihan: true, hint: "dipilih di atas" },
  { key: "brand", label: "Brand", pilihan: true, hint: "dipilih di atas" },
  { key: "tipe", label: "Tipe", hint: "mis. iPhone 15 Pro Max" },
  { key: "ram_storage", label: "Storage", hint: "mis. 8/256GB" },
  { key: "warna", label: "Warna", hint: "" },
  { key: "distributor", label: "Distributor", hint: "Opsional" },
];

const PEMISAH_SKU_FALLBACK = "/";
const PEMISAH_NAMA_FALLBACK = " / ";
// Kode kategori BAWAAN — cermin `master_opsi.py::KODE_KATEGORI_BAWAAN`. Owner
// menyebut keempatnya sendiri di brief 4 Sep, jadi pratinjau harus ikut
// menampilkannya walau registry belum diisi; kalau tidak, layar bilang "SKU
// tidak bisa dibentuk" untuk produk yang sebenarnya tersimpan mulus.
const KODE_KATEGORI_BAWAAN_FALLBACK = { handphone: "HP", tab: "TAB", laptop: "LT", wearable: "WR" };

// Peta pencarian {dimensi: {nilai_huruf_kecil: KODE}} — meniru
// `master_opsi.py::peta_kode_sku` (pencocokan nilai case-insensitive).
function petaKodeSku(opsi) {
  const peta = {};
  // Bawaan kategori dipasang DULU supaya entri registry di bawah bisa
  // menimpanya — termasuk menimpa dengan kode kosong, yang artinya "sengaja
  // tanpa kode" (tiga keadaan yang sama dengan backend).
  peta.kategori = { ...KODE_KATEGORI_BAWAAN_FALLBACK };
  for (const it of (opsi.kode_sku || [])) {
    const dim = String(it?.dimensi || "").trim();
    const nilai = String(it?.nilai || "").trim();
    const kode = String(it?.kode || "").trim();
    if (!dim || !nilai) continue;
    if (!kode) {
      if (dim === "kategori") peta.kategori[nilai.toLowerCase()] = "";
      continue;
    }
    if (!peta[dim]) peta[dim] = {};
    peta[dim][nilai.toLowerCase()] = kode;
  }
  return peta;
}

// Poin 5 — pratinjau nama otomatis, URUTAN PERSIS meniru backend
// `products.py::_bangun_nama_otomatis` (Brand + Type + Ram/Storage + Warna +
// Distributor) supaya apa yang staf lihat di form SAMA dengan yang tersimpan.
// Poin 2 brief 31 Agu: pemisahnya " / ", bukan spasi — nilainya diambil dari
// backend (`pemisah_nama`) supaya kalau owner mengubah gayanya nanti cukup
// satu tempat, bukan dua.
function namaOtomatisPreview(opsi, brand, tipe, ramStorage, warna, distributor, kategoriUtama) {
  // Brief 4 Sep: segmen PERTAMA nama adalah KODE Kategori Utama (`HP`), bukan
  // labelnya — persis contoh owner `HP / APPLE / IPHONE 13 / …`.
  const kodeKategori = (petaKodeSku(opsi).kategori || {})[String(kategoriUtama || "").toLowerCase()];
  return [kodeKategori, brand, tipe, ramStorage, warna, distributor]
    .map(b => String(b || "").trim())
    .filter(Boolean)
    .join(opsi?.pemisah_nama || PEMISAH_NAMA_FALLBACK);
}

// Poin 1 — pratinjau SKU otomatis, meniru `products.py::_bangun_sku_dari_kode`.
// Backend TETAP jadi penentu (perakitnya di sana, dan dia juga yang menangani
// tabrakan SKU) — ini murni supaya staf melihat `APL/13/128/PINK/TAM` SEBELUM
// menekan Simpan, dan tahu lebih awal kalau ada nilai yang kodenya belum
// didaftarkan owner. Kalau aturan perakitnya berubah di backend, ubah juga di
// sini (pola sama dengan `daftarBrandUntukKategoriUtama`).
//
// Kembalian: { aktif, sku, kurang[] } — `aktif:false` berarti peta kode masih
// kosong, jadi SKU dibentuk backend dengan bentuk LAMA dan tidak ada yang
// berguna untuk dipratinjau di sini.
// P1/P2 (brief owner 12 Sep 2026) — enam sub-kolom, meniru
// `products.py::_bahan_sku_efektif`. Tiga tingkat sumber: ketikan staf pada
// produk ini, lalu kode dari master (keseragaman singkatan, keputusan owner
// 31 Agu), lalu slug nilai namanya sendiri.
//
// Tingkat ketiga itu yang membuat blok "Kode SKU per kolom" bisa dihapus tanpa
// membuat produk bermerek baru mustahil disimpan: dulu nilai tanpa kode
// DITOLAK 400, dan satu-satunya cara mendaftarkannya adalah blok yang justru
// diminta owner untuk dihapus.
function slugSku(s) {
  return String(s || "").replace(/[^A-Za-z0-9]+/g, "").toUpperCase();
}

function bahanSkuEfektif(opsi, form) {
  const peta = petaKodeSku(opsi);
  const nilaiNama = {
    kategori: form.kategori_utama, brand: form.brand, tipe: form.tipe,
    ram_storage: form.ram_storage, warna: form.warna, distributor: form.distributor,
  };
  const hasil = {};
  for (const d of DIMENSI_BAHAN) {
    const manual = String(form[`sku_${d.key}`] || "").trim();
    if (manual) { hasil[d.key] = slugSku(manual); continue; }
    const v = String(nilaiNama[d.key] || "").trim();
    hasil[d.key] = v ? ((peta[d.key] || {})[v.toLowerCase()] || slugSku(v)) : "";
  }
  return hasil;
}

function skuOtomatisPreview(opsi, form) {
  const bahan = bahanSkuEfektif(opsi, form);
  const sku = DIMENSI_BAHAN.map(d => bahan[d.key]).filter(Boolean)
    .join(opsi?.pemisah_sku || PEMISAH_SKU_FALLBACK);
  return { aktif: !!sku, sku, bahan, kurang: [] };
}

// Poin 1 & 6 — daftar brand relevan untuk Kategori Utama terpilih. Meniru
// `daftar_brand_untuk_kategori()` (routers/inventory/master_opsi.py) SEPERSIS
// mungkin (Accessories -> daftar Accessories, sisanya -> daftar Gadget,
// jatuh ke daftar `brand` lama kalau split-nya masih kosong) — tidak ada
// endpoint khusus yang mengembalikan daftar sudah tersaring per kategori,
// jadi pemilihan 2-cabang ini TERPAKSA hidup di sini juga. Kalau aturan
// pemilihannya berubah di backend, ubah juga di sini.
function daftarBrandUntukKategoriUtama(opsi, kategoriUtama) {
  const daftar = kategoriUtama === "accessories"
    ? (opsi.brand_accessories || [])
    : (opsi.brand_gadget || []);
  return daftar.length ? daftar : (opsi.brand || []);
}

const kosong = {
  sku: "", nama: "", sub_kategori: "", brand: "",
  // Hierarki kategori (poin 2-4) — default sama seperti perilaku LAMA
  // ("Unit Baru" = Handphone + New) supaya form Produk Baru tidak berubah
  // kebiasaan default staf.
  kategori_utama: "handphone", status_kondisi: "baru", varian_second: "",
  // Poin 5 — empat bahan nama/SKU otomatis, hanya dipakai kategori gadget.
  tipe: "", ram_storage: "", warna: "", distributor: "",
  // P1/P2 (brief owner 12 Sep 2026) — enam bahan SKU milik PRODUK. Kosong =
  // "ikut saja singkatan dari master"; diisi = ketikan staf yang menang.
  sku_kategori: "", sku_brand: "", sku_tipe: "", sku_ram_storage: "",
  sku_warna: "", sku_distributor: "",
  harga_beli: "", harga_jual: "", has_imei: false, identifier_type: "imei",
  barcode: "", satuan: "pcs", min_stock: 0,
  // Ambang stok maksimum (brief owner 14 Agu #8) — "" = tanpa batas atas.
  max_stock: "",
  costing_method: "",
  // Rasio konversi satuan besar->kecil (brief bos #25), mis. 1 dus = 12 pcs —
  // keduanya opsional tapi harus terisi bersamaan (ditegakkan backend).
  satuan_besar: "", isi_per_satuan_besar: "",
};

// ============ Input rupiah berformat (poin 11) ============
// Tampil "12.500", tersimpan "12500". Format id-ID dipasang di SATU komponen
// supaya seluruh input harga berperilaku sama — salah ketik nol berlebih
// langsung kelihatan dari titik ribuannya.
function formatRibuan(v) {
  const d = String(v ?? "").replace(/\D/g, "");
  return d ? Number(d).toLocaleString("id-ID") : "";
}

function InputRupiah({ value, onChange, testid, placeholder, id, ariaLabel }) {
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">Rp</span>
      <input id={id} inputMode="numeric" value={formatRibuan(value)} placeholder={placeholder || "0"}
             onChange={e => onChange(e.target.value.replace(/\D/g, ""))}
             data-testid={testid} aria-label={ariaLabel}
             className={`${inp} pl-9 text-right font-mono`} />
    </div>
  );
}

// ============ Badge margin (brief bos 21 Agu #B1) ============
// Batas margin minimum PER KATEGORI (price floor) DICABUT SELURUHNYA — badge
// kuning "margin tipis tapi masih di bawah ambang" tidak ada lagi. Satu-
// satunya peringatan yang tersisa: `p.rugi` (harga jual < modal SEBENARNYA,
// brief owner 17 Agu #1), karena itu satu-satunya sinyal yang tidak pernah
// jadi tebakan — beda dari ambang persentase per kategori yang dulu dikarang.
// `suffix` = "-m" untuk versi kartu supaya testid tidak menabrak versi tabel
// yang tampil bersamaan di DOM (konvensi `responsive-table.jsx`).
// `bentuk` (batch P, brief 18 Sep 2026): 'kelas' -> lencana Rugi/Tipis/Normal/
// Tebal TANPA angka (dari `p.margin_kelas`, dihitung backend sebelum
// `margin_persen` dibuang — akses_modal.py::terapkan_ke_produk). Bawaan
// (undefined/'persen') = perilaku LAMA persis, supaya akun tingkat 'lengkap'
// tidak berubah tampilan sama sekali.
const KELAS_WARNA_PRODUK = {
  rugi: "bg-rose-50 text-rose-700 border-rose-200",
  tipis: "bg-amber-50 text-amber-700 border-amber-200",
  normal: "bg-slate-100 text-slate-700 border-slate-200",
  tebal: "bg-emerald-50 text-emerald-700 border-emerald-200",
  belum_diketahui: "bg-slate-50 text-slate-400 border-slate-200",
};

function MarginBadge({ p, suffix = "", bentuk }) {
  if (bentuk === "kelas") {
    const k = p.margin_kelas || { kode: "belum_diketahui", label: "—" };
    return (
      <span data-testid={`inv-prod-kelas-${p.sku}${suffix}`}
        title={k.kode === "belum_diketahui" ? "modal belum diketahui — bukan berarti untung" : ""}
        className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full border ${KELAS_WARNA_PRODUK[k.kode] || KELAS_WARNA_PRODUK.belum_diketahui}`}>
        {k.kode === "belum_diketahui" ? "—" : k.label}
      </span>
    );
  }
  const modalLabel = p.modal_sumber === "perkiraan" ? "PERKIRAAN dari harga beli" : "aktual";
  if (p.modal_sumber === "belum_diketahui") {
    return (
      <span data-testid={`inv-prod-modal-unknown-${p.sku}${suffix}`}
            title="Belum pernah ada penerimaan barang untuk SKU ini DAN harga beli kosong — modal tidak diketahui, bukan nol. Tidak ditandai rugi sampai ada datanya."
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-semibold">
        <HelpCircle className="w-3 h-3" /> modal ?
      </span>
    );
  }
  if (p.rugi) {
    return (
      <span data-testid={`inv-prod-rugi-${p.sku}${suffix}`}
            title={`RUGI — margin ${p.margin_persen}% (modal ${modalLabel}): harga jual di BAWAH modal, tiap unit terjual mengurangi kas, bukan sekadar kurang untung`}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-rose-700 text-white text-[11px] font-bold">
        <TrendingDown className="w-3.5 h-3.5" /> RUGI {p.margin_persen}%
      </span>
    );
  }
  return (
    <span data-testid={`inv-prod-margin-${p.sku}${suffix}`}
          title={p.margin_persen == null ? "" : `Modal ${modalLabel}`}
          className="font-mono text-slate-700">
      {p.margin_persen == null ? "—" : `${p.margin_persen}%`}
      {p.modal_sumber === "perkiraan" && <span className="text-[10px] text-amber-600"> (est.)</span>}
    </span>
  );
}

// ============ Modal per unit (poin 19 Agu #2) ============
// Kolom lama menampilkan `stock_nilai` (qty x modal = TOTAL nilai stok SKU
// itu) di bawah label "Nilai Modal" — gampang dibaca sebagai modal SATU unit,
// padahal untuk SKU bermodal besar dengan stok banyak angkanya bisa berlipat-
// lipat dari harga sebenarnya. Yang dipakai di sini adalah `modal_pakai`
// (backend `_hitung_margin`, SATU sumber kebenaran yang sama dipakai
// `MarginBadge` di atas): rata-rata modal AKTUAL dari lapisan biaya kalau ada,
// baru jatuh ke `harga_beli` manual sebagai PERKIRAAN kalau SKU belum pernah
// punya penerimaan barang — dan ditandai jujur lewat `modal_sumber`, jangan
// disamarkan seolah-olah aktual. Total nilai stok TETAP ditampilkan (dulu
// satu-satunya info kolom ini) sebagai baris kecil kedua supaya tidak ada
// informasi yang hilang diam-diam, hanya dipindah jadi info sekunder.
function ModalUnitCell({ p, suffix = "" }) {
  if (p.modal_sumber === "belum_diketahui") {
    return (
      <span data-testid={`inv-prod-modalunit-unknown-${p.sku}${suffix}`}
            title="Belum pernah ada penerimaan barang untuk SKU ini DAN harga beli kosong — modal per unit tidak diketahui, bukan nol."
            className="inline-flex items-center justify-end gap-1 text-slate-400 text-xs">
        <HelpCircle className="w-3.5 h-3.5" /> modal ?
      </span>
    );
  }
  return (
    <div data-testid={`inv-prod-modalunit-${p.sku}${suffix}`} className="text-right">
      <p className="font-mono text-slate-700"
         title={p.modal_sumber === "perkiraan"
           ? "PERKIRAAN dari harga beli manual — SKU ini belum pernah punya penerimaan barang sungguhan"
           : "Rata-rata modal AKTUAL dari lapisan biaya penerimaan barang"}>
        {idr(p.modal_pakai)}
        {p.modal_sumber === "perkiraan" && <span className="text-[10px] text-amber-600"> (est.)</span>}
      </p>
      {p.stock_qty > 0 && (
        <p className="text-[10px] text-slate-400"
           title="Total nilai seluruh stok SKU ini (modal per unit x qty tersisa)">
          Total {idr(p.stock_nilai)}
        </p>
      )}
    </div>
  );
}

export default function InventoryProducts() {
  const { can, user } = useAuth();
  // Merge Brand digerbangi admin/superadmin SAJA (backend `require_role`,
  // BUKAN `require_permission("inventory_produk", ...)`) — eksekusinya
  // menulis ke koleksi Finance (cashback, opname) selain Inventory, jadi
  // tombolnya juga harus disembunyikan untuk role lain (aturan #3 CLAUDE.md:
  // jangan tampilkan tombol yang ujungnya 403). Presedennya persis
  // `Supplier.jsx::isAdmin` untuk "Gabung Supplier".
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const queryClient = useQueryClient();
  // qInput = teks kotak pencarian (apa adanya, per ketikan).
  // qApplied = kata kunci yang benar-benar dikirim ke backend (dan masuk queryKey) —
  // menyusul qInput setelah debounce 400ms "diam", atau langsung disamakan tanpa
  // menunggu untuk aksi yang harus instan (tekan Enter, hasil scan barcode).
  const [qInput, setQInput] = useState("");
  const [qApplied, setQApplied] = useState("");
  const qDebounced = useDebounce(qInput, 400);
  useEffect(() => { setQApplied(qDebounced); }, [qDebounced]);

  const [filter, setFilter] = useState({
    kategori: "", sub_kategori: "", brand: "", warehouse_id: "", supplier: "",
    only_aging: false, include_inactive: false, sort: "nama", arah: "asc",
    // P4 (brief 4 Sep): "" = semua | kosong (= 0) | ada (> 0) | minus (< 0)
    stok: "",
  });
  const [page, setPage] = useState(1);
  const [form, setForm] = useState(null);
  // Batch P (18 Sep 2026): apakah kolom Harga Beli boleh dikirim untuk form
  // yang SEDANG TERBUKA ini — BUKAN field `form` (kalau ikut di-spread ke
  // body POST, backend akan menganggapnya bagian ProductReq). Produk BARU
  // selalu true (pembuatnya sendiri yang memasok angka, § LAYAR C); produk
  // LAMA mengikuti `modal_terlihat` daftar tempat baris itu berasal SAAT
  // form dibuka — race dengan Bos mengubah pengaturan di antaranya tetap
  // aman karena Lapis 2 backend (`_upsert_data`) membaca ulang tingkat per
  // permintaan, bukan mempercayai flag ini.
  const [hbeliTampil, setHbeliTampil] = useState(true);
  const [busy, setBusy] = useState(false);
  const [labelFor, setLabelFor] = useState(null);
  const [pilih, setPilih] = useState([]);
  const [showPengaturan, setShowPengaturan] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [rincianFor, setRincianFor] = useState(null);   // P5: produk yang rinciannya dibuka
  const [showUsulanMinStock, setShowUsulanMinStock] = useState(false);
  // modal merge brand: { sourceNama, targetNama, preview, previewLoading, executing }
  // — bentuk state SENGAJA mengikuti `mergeModal` di Supplier.jsx (Pola A).
  const [mergeBrand, setMergeBrand] = useState(null);

  // Brand/satuan/kategori custom/ambang umur + gudang + supplier — dipakai untuk
  // dropdown filter & form. Jarang berubah, jadi cache React Query pas: hanya
  // ditembak ulang saat Pengaturan disimpan (lihat PengaturanModal onClose).
  // Kegagalan di sini tidak boleh mengganggu daftar utama — dropdown jatuh
  // balik ke nilai bawaan (perilaku sama seperti sebelum migrasi, tanpa toast).
  // TAPI diam-diam kosong bukan pilihan: `.catch(() => null)` di bawah membuat
  // dropdown gudang/supplier lenyap tanpa bisa dibedakan dari "memang belum ada
  // datanya". Kegagalannya sekarang DICATAT (gudangGagal/supplierGagal) dan
  // dipakai untuk memunculkan keterangan singkat di baris filter.
  const { data: opsiData, refetch: refetchOpsi } = useDataQuery(
    OPSI_QK,
    async () => {
      const [o, g, s] = await Promise.all([
        api.get("/inventory/master-opsi"),
        api.get("/inventory/warehouses").catch(() => null),
        api.get("/supplier").catch(() => null),
      ]);
      return {
        opsi: o.data, gudang: g?.data.items || [], suppliers: s?.data.items || [],
        gudangGagal: g === null, supplierGagal: s === null,
      };
    },
  );
  const opsi = opsiData?.opsi ?? OPSI_KOSONG;
  const gudang = opsiData?.gudang ?? [];
  const suppliers = opsiData?.suppliers ?? [];
  const gudangGagal = opsiData?.gudangGagal ?? false;
  const supplierGagal = opsiData?.supplierGagal ?? false;

  // Hierarki kategori (poin 2-4) — pakai jawaban backend kalau sudah ada,
  // fallback statis hanya sebelum request pertama selesai (lihat komentar
  // di definisi KATEGORI_UTAMA_FALLBACK).
  const kategoriUtamaList = opsi.kategori_utama?.length ? opsi.kategori_utama : KATEGORI_UTAMA_FALLBACK;
  const varianSecondList = opsi.varian_second?.length ? opsi.varian_second : VARIAN_SECOND_FALLBACK;
  // Kunci Kategori Utama yang wajib New/Second (poin 3) — diturunkan dari
  // flag `wajib_status` backend, BUKAN daftar hardcode di frontend, supaya
  // kalau owner menambah/mengurangi kategori gadget cukup diubah di satu
  // tempat (`master_opsi.py::KATEGORI_GADGET`).
  const gadgetKeySet = new Set(kategoriUtamaList.filter(k => k.wajib_status).map(k => k.key));
  // Opsi kategori RATA (poin 7: "Filter Kategori — urutan logis, bukan A-Z")
  // dipakai untuk filter tabel DAN badge kategori di baris — dua entri
  // New/Second per Kategori Utama gadget, satu entri polos untuk sisanya,
  // urutannya persis urutan `kategoriUtamaList` (yang sudah logis dari
  // backend), bukan diurut ulang alfabet.
  const kategoriFlatOptions = kategoriUtamaList.flatMap(k => k.wajib_status
    ? [{ v: `${k.key}_baru`, l: `${k.label} - New` }, { v: `${k.key}_second`, l: `${k.label} - Second` }]
    : [{ v: k.key, l: k.label }]);
  // Filter/Bulk Harga/Usulan Min Stok butuh SATU daftar brand lintas kategori
  // (bukan tersaring per Kategori Utama seperti di form produk) — union kedua
  // daftar split, jatuh ke daftar `brand` legacy kalau split-nya masih kosong
  // (SAMA pola fallback dengan `daftarBrandUntukKategoriUtama`), supaya brand
  // yang baru didaftarkan lewat Pengaturan (sekarang hanya lewat dua input
  // Gadget/Accessories, poin 6) tetap muncul di sini.
  const brandFilterList = (() => {
    const gab = Array.from(new Set([...(opsi.brand_gadget || []), ...(opsi.brand_accessories || [])]));
    return gab.length ? gab : (opsi.brand || []);
  })();
  // Pilihan dropdown FILTER (brief 22 Sep 2026): dari server —
  // `GET /master-opsi`.filter_produk, penurun yang SAMA dengan dropdown Price
  // List (`master_opsi.opsi_filter_produk`), supaya kedua layar mustahil
  // menawarkan daftar berbeda. Dua turunan lokal di atas hanya CADANGAN
  // sebelum respons pertama tiba (pola sama dengan KATEGORI_UTAMA_FALLBACK).
  const opsiFilter = opsiDariServer(opsi.filter_produk)
    || { kategori: kategoriFlatOptions, kategoriCustom: opsi.kategori_custom || [], brand: brandFilterList };

  const produkParams = {
    q: qApplied || undefined,
    kategori: filter.kategori || undefined,
    sub_kategori: filter.sub_kategori || undefined,
    brand: filter.brand || undefined,
    warehouse_id: filter.warehouse_id || undefined,
    supplier: filter.supplier || undefined,
    stok: filter.stok || undefined,
    only_aging: filter.only_aging || undefined,
    include_inactive: filter.include_inactive || undefined,
    sort: filter.sort, arah: filter.arah,
    page, per_page: 50,
  };
  const { data: produkData, loading, error: produkError } = useDataQuery(
    [PRODUK_QK, produkParams],
    () => api.get("/inventory/products", { params: produkParams }).then(r => r.data),
  );
  useEffect(() => {
    if (produkError) toast.error(produkError?.response?.data?.detail || "Gagal memuat produk");
  }, [produkError]);
  const items = produkData?.items || [];
  // Flag akses modal & margin (batch P, brief 18 Sep 2026) — SATU sumber dari
  // server, layar tidak pernah menebak/menghitung ulang siapa boleh lihat apa
  // (aturan 3 § LAYAR C DESAIN-BRIEF-18-SEP.md).
  const modalTerlihat = !!produkData?.modal_terlihat;
  const marginBentuk = produkData?.margin_bentuk || null;
  const catatanAkses = produkData?.catatan_akses || null;
  const meta = {
    total: produkData?.total || 0,
    page: produkData?.page || page,
    total_pages: produkData?.total_pages || 1,
    aging_hari: produkData?.aging_hari || 60,
  };

  const invalidateProduk = () => {
    queryClient.invalidateQueries({ queryKey: [PRODUK_QK] });
  };

  const ubahFilter = (patch) => { setPage(1); setFilter(f => ({ ...f, ...patch })); };

  const simpan = async () => {
    const gadgetTerpilih = gadgetKeySet.has(form.kategori_utama);
    const labelKategoriUtama = kategoriUtamaList.find(k => k.key === form.kategori_utama)?.label || form.kategori_utama;
    // Cek dini di klien — modal ini bukan <form>, jadi atribut `required`
    // pada field Brand tidak memicu validasi native browser. Backend tetap
    // jadi penjaga sesungguhnya (`products.py::_validate`); ini cuma
    // menghindari bolak-balik jaringan DAN mencegah toast 400 mentah untuk
    // kesalahan yang sudah pasti ditolak (poin 3/5 brief 23-24 Agu). Pesannya
    // sengaja meniru bahasa backend supaya konsisten kalau sampai lolos ke sana.
    if (!form.kategori_utama) {
      toast.error("Pilih Kategori dulu");
      return;
    }
    if (gadgetTerpilih && !form.status_kondisi) {
      toast.error(`Kategori ${labelKategoriUtama} wajib memilih sub-kategori New atau Second sebelum bisa disimpan.`);
      return;
    }
    if (gadgetTerpilih && form.status_kondisi === "second" && !(form.varian_second || "").trim()) {
      // Kalimat PERSIS diminta brief (poin 3) — juga dipakai sebagai hint
      // permanen di bawah select Varian Second (lihat render form).
      toast.error("Kondisi unit second ini belum tercatat — pilih dulu.");
      return;
    }
    if (gadgetTerpilih) {
      const hilang = [["Type", form.tipe], ["Ram/Storage", form.ram_storage], ["Warna", form.warna]]
        .filter(([, v]) => !(v || "").trim()).map(([label]) => label);
      if (hilang.length) {
        toast.error(`Kategori ${labelKategoriUtama} wajib mengisi ${hilang.join(", ")} — dipakai membentuk Nama & SKU produk otomatis.`);
        return;
      }
    } else {
      if (!(form.nama || "").trim()) { toast.error("Nama produk wajib diisi"); return; }
      if (!(form.sku || "").trim()) { toast.error("SKU wajib diisi"); return; }
    }
    // HANYA untuk produk BARU (`!form.id`) — produk lama tanpa brand tidak
    // boleh mendadak diblokir saat mengedit field lain (keputusan owner D2).
    if (!form.id && !(form.brand || "").trim()) {
      toast.error("Brand wajib diisi untuk produk baru");
      return;
    }
    setBusy(true);
    try {
      const kategoriKomposit = komposKategori(form.kategori_utama, form.status_kondisi, gadgetKeySet);
      const body = {
        ...form,
        kategori: kategoriKomposit,
        // Pratinjau nama (poin 5) yang dikirim server hanya untuk kerapian —
        // backend TETAP membangun ulang nama gadget dari Brand/Type/Ram-
        // Storage/Warna/Distributor sendiri (`_siapkan_nama_dan_sku`), jadi
        // nilai ini tidak pernah jadi sumber kebenaran, hanya supaya body
        // yang dikirim tidak membawa nama lama yang sudah tidak relevan.
        nama: gadgetTerpilih ? namaOtomatisPreview(opsi, form.brand, form.tipe, form.ram_storage, form.warna, form.distributor, form.kategori_utama) : form.nama,
        varian_second: gadgetTerpilih && form.status_kondisi === "second" ? form.varian_second : null,
        tipe: gadgetTerpilih ? (form.tipe || null) : null,
        ram_storage: gadgetTerpilih ? (form.ram_storage || null) : null,
        warna: gadgetTerpilih ? (form.warna || null) : null,
        distributor: gadgetTerpilih ? (form.distributor || null) : null,
        // Bahan SKU hanya berlaku untuk kategori gadget — sama seperti bahan
        // namanya. Kosong dikirim `null`, BUKAN "": backend membedakan "belum
        // diisi" (ikut singkatan master) dari "sengaja dikosongkan".
        ...Object.fromEntries(DIMENSI_BAHAN.map(d => [
          `sku_${d.key}`, gadgetTerpilih ? (form[`sku_${d.key}`] || null) : null,
        ])),
        // § LAYAR C / §6 DESAIN-BRIEF-18-SEP.md: akun yang tidak melihat modal
        // TIDAK mengirim kunci ini sama sekali (bukan 0) — backend membedakan
        // "tidak dikirim = pertahankan" dari "0 eksplisit" lewat
        // `ProductReq.harga_beli: Optional[float] = None`. Dihapus lagi di
        // bawah kalau `hbeliTampil` false.
        harga_beli: Number(form.harga_beli) || 0,
        harga_jual: Number(form.harga_jual) || 0,
        min_stock: Number(form.min_stock) || 0,
        // "" (belum diisi) -> null (tanpa batas atas), BUKAN 0 — 0 akan
        // menolak hampir semua min_stock lewat validasi "maks < min" backend.
        max_stock: form.max_stock === "" || form.max_stock == null ? null : Number(form.max_stock),
        satuan_besar: form.satuan_besar || null,
        isi_per_satuan_besar: form.isi_per_satuan_besar ? Number(form.isi_per_satuan_besar) : null,
      };
      // Field UI-saja (bukan bagian `ProductReq` backend) — dibuang supaya
      // body yang dikirim persis bentuk yang backend harapkan.
      delete body.kategori_utama;
      delete body.status_kondisi;
      // § LAYAR C: kolom Harga Beli tersembunyi -> jangan kirim kuncinya sama
      // sekali (bukan 0) supaya `ProductReq.harga_beli=None` (Lapis 1
      // penjaga backend) mempertahankan nilai lama, bukan menimpanya.
      if (!hbeliTampil) delete body.harga_beli;
      const r = await api.post("/inventory/products", body);
      toast.success(r.data.message);
      if (r.data.harga_beli_diabaikan) {
        toast.warning(
          <span data-testid="inv-f-hbeli-diabaikan">Harga beli tidak diubah — akun Anda tidak melihat modal</span>,
        );
      }
      setForm(null);
      invalidateProduk();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setBusy(false); }
  };

  // Toggle dua arah (brief bos #23) lewat PATCH /status — menggantikan DELETE
  // yang cuma satu arah. Nonaktifkan ditolak backend kalau produk masih
  // berstok; pesan penolakannya ditampilkan apa adanya lewat toast.
  const toggleAktif = async (p) => {
    const target = p.is_active === false; // sedang nonaktif -> aktifkan; sedang aktif -> nonaktifkan
    if (!target && !window.confirm(`Nonaktifkan produk ${p.sku} — ${p.nama}?`)) return;
    try {
      const r = await api.patch(`/inventory/products/${p.sku}/status`, { is_active: target });
      toast.success(r.data.message);
      invalidateProduk();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah status produk"); }
  };

  const generateBarcode = async (p) => {
    try {
      const r = await api.post(`/inventory/products/${p.sku}/generate-barcode`);
      toast.success(r.data.message);
      invalidateProduk();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat barcode"); }
  };

  const cetakLabel = async (skus, ukuran = "sedang", per_item = 1) => {
    try {
      const token = localStorage.getItem("hris_token");
      const res = await fetch(`${API}/inventory/barcode/labels`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ skus, ukuran, per_item }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Gagal cetak");
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(blob);
      // SKU rakitan mengandung garis miring (`APL/13/128/PINK/TAM`, poin 1
      // brief 31 Agu) — "/" di dalam `a.download` diperlakukan browser
      // sebagai pemisah folder dan hasilnya nama berkas yang aneh/terpotong.
      // Diratakan jadi "-" HANYA untuk nama berkas unduhan; SKU yang dikirim
      // ke server (`skus` di atas) tetap apa adanya.
      a.download = `Label_${skus.join("_").replace(/\//g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(a.href);
      setLabelFor(null);
    } catch (e) { toast.error(e.message); }
  };

  const onScan = async (kode) => {
    try {
      const r = await api.get("/inventory/barcode/cari", { params: { kode } });
      toast.success(r.data.pesan);
      if (r.data.tipe === "produk") { setQInput(r.data.produk.sku); setQApplied(r.data.produk.sku); setPage(1); }
    } catch (e) {
      // Kode belum terdaftar -> tawarkan langsung jadi produk baru
      toast.info(`Kode ${kode} belum terdaftar — dibuka sebagai produk baru`);
      setHbeliTampil(true); // produk BARU: pembuatnya sendiri yang memasok angka (§ LAYAR C)
      setForm({ ...kosong, barcode: kode });
    }
  };

  // Buka form Ubah untuk produk `p` — mengurai `kategori` mentah jadi
  // {kategori_utama, status_kondisi} (poin 2-4, lewat `uraiKategori`) dan
  // menormalkan field nullable (poin 5: 564 produk lama belum punya Type/
  // Ram-Storage/Warna, poin 3: 91 produk second belum punya varian) jadi
  // string kosong — controlled input React menolak `undefined`/`null`.
  const bukaEdit = (p) => {
    // § LAYAR C: hbeliTampil datang dari respons DAFTAR tempat `p` berasal
    // (daftar ini seragam satu `modal_terlihat` untuk semua barisnya), bukan
    // dihitung ulang di sini.
    setHbeliTampil(modalTerlihat);
    setForm({
    ...kosong, ...p, ...uraiKategori(p),
    varian_second: p.varian_second || "",
    tipe: p.tipe || "", ram_storage: p.ram_storage || "", warna: p.warna || "", distributor: p.distributor || "",
    sku_kategori: p.sku_kategori || "", sku_brand: p.sku_brand || "",
    sku_tipe: p.sku_tipe || "", sku_ram_storage: p.sku_ram_storage || "",
    sku_warna: p.sku_warna || "", sku_distributor: p.sku_distributor || "",
    sub_kategori: p.sub_kategori || "",
    });
  };

  const toggle = (sku) => setPilih(p => p.includes(sku) ? p.filter(x => x !== sku) : [...p, sku]);

  // ---- Merge Brand (keputusan owner D2, brief 21 Agu — Pola A, meniru
  // Supplier.jsx::mergeModal). Dua tahap: pratinjau TIDAK menulis apa pun,
  // tombol eksekusi baru aktif setelah pratinjau bilang aman. Mengubah
  // sumber/target APA PUN membatalkan pratinjau lama supaya staf tidak
  // mengeksekusi hasil pratinjau yang sudah basi. ----
  const bukaMergeBrand = (prefillSource = "") => setMergeBrand({
    sourceNama: prefillSource, targetNama: "",
    preview: null, previewLoading: false, executing: false,
  });
  const ubahMergeBrand = (patch) => setMergeBrand(m => (m ? { ...m, ...patch, preview: null } : m));

  const previewMergeBrand = async () => {
    const m = mergeBrand;
    if (!m) return;
    if (!m.sourceNama.trim() || !m.targetNama.trim()) {
      toast.error("Isi brand sumber dan target dulu");
      return;
    }
    setMergeBrand(cur => ({ ...cur, previewLoading: true }));
    try {
      const r = await api.post("/inventory/master-opsi/brand/merge/preview",
        { source_nama: m.sourceNama, target_nama: m.targetNama });
      setMergeBrand(cur => ({ ...cur, preview: r.data, previewLoading: false }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat pratinjau merge brand");
      setMergeBrand(cur => ({ ...cur, previewLoading: false }));
    }
  };

  const eksekusiMergeBrand = async () => {
    const m = mergeBrand;
    if (!m?.preview?.bisa_eksekusi) return;
    setMergeBrand(cur => ({ ...cur, executing: true }));
    try {
      const r = await api.post("/inventory/master-opsi/brand/merge/execute",
        { source_nama: m.sourceNama, target_nama: m.targetNama });
      toast.success(r.data.message || "Brand digabung");
      setMergeBrand(null);
      refetchOpsi();
      invalidateProduk();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menggabungkan brand");
      setMergeBrand(cur => ({ ...cur, executing: false }));
    }
  };

  // Turunan form Produk (poin 2-6) — dihitung DI LUAR JSX (bukan di dalam
  // `{form && (...)}`, yang murni ekspresi dan tidak bisa punya `const` lokal)
  // supaya markup form di bawah tetap terbaca. Dijaga `form ? ... : ...`
  // karena `form` bisa `null` (modal tertutup).
  const gadgetTerpilihForm = form ? gadgetKeySet.has(form.kategori_utama) : false;
  const labelKategoriUtamaForm = form
    ? (kategoriUtamaList.find(k => k.key === form.kategori_utama)?.label || form.kategori_utama) : "";
  const namaPreviewForm = form
    ? namaOtomatisPreview(opsi, form.brand, form.tipe, form.ram_storage, form.warna, form.distributor,
                          form.kategori_utama) : "";
  // Pratinjau SKU hanya bermakna kalau SKU-nya memang akan DIRAKIT backend —
  // yaitu kategori gadget DAN field SKU dikirim kosong (`_siapkan_nama_dan_sku`
  // tidak pernah menyentuh SKU yang sudah terisi, termasuk saat mengedit
  // produk lama yang SKU-nya terkunci).
  const skuAkanDirakit = !!form && gadgetTerpilihForm && !(form.sku || "").trim();
  const skuPreviewForm = skuAkanDirakit
    ? skuOtomatisPreview(opsi, form)
    : { aktif: false, sku: "", kurang: [] };
  const brandListForm = form ? daftarBrandUntukKategoriUtama(opsi, form.kategori_utama) : [];
  // P1/P2: Kategori Utama kini punya DUA select (di kepala form dan di blok
  // bahan Nama/SKU). Handlernya SATU — dua select dengan onChange yang
  // disalin pasti berpisah jalan, dan yang hilang di salah satunya adalah
  // pembersihan `status_kondisi`/`varian_second`/`brand` yang menjaga
  // kategori & brand tetap sepadan.
  const ubahKategoriUtama = (baru) => {
    const gadgetBaru = gadgetKeySet.has(baru);
    setForm(f => ({
      ...f, kategori_utama: baru,
      status_kondisi: "", varian_second: "",
      tipe: gadgetBaru ? f.tipe : "", ram_storage: gadgetBaru ? f.ram_storage : "",
      warna: gadgetBaru ? f.warna : "", distributor: gadgetBaru ? f.distributor : "",
      // Daftar brand beda per kategori (poin 1/6) — brand lama belum tentu
      // ada di daftar kategori yang baru dipilih.
      brand: "",
    }));
  };

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="inventory-products">
      <div className="flex items-start justify-between flex-col lg:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Inventory</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Master Produk</h1>
          <p className="text-sm text-slate-600 mt-1">
            Barang ber-IMEI otomatis memakai Actual Cost — modal melekat pada tiap unit.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Komponen ini sudah membawa tombol + modal kameranya sendiri */}
          <BarcodeScanner onDetected={onScan} testId="inv-prod-scan"
                          title="Scan barcode produk atau IMEI unit" />
          {pilih.length > 0 && (
            <button onClick={() => setLabelFor({ skus: pilih })} data-testid="inv-prod-label-bulk"
                    className="h-10 px-4 rounded-lg border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-sm font-medium text-emerald-700 inline-flex items-center gap-2">
              <Printer className="w-4 h-4" /> Cetak {pilih.length} label
            </button>
          )}
          {can("inventory_produk", "create") && (
            <>
              <button onClick={() => setShowPengaturan(true)} data-testid="inv-prod-pengaturan"
                      title="Kelola brand, satuan, kategori custom, ambang umur stok"
                      className="h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-1.5">
                <Settings2 className="w-4 h-4" /> Pengaturan
              </button>
              {isAdmin && (
                <button onClick={() => bukaMergeBrand()} data-testid="inv-prod-merge-brand-open"
                        title="Gabungkan dua ejaan brand duplikat (mis. 'Apple' ke 'APPLE')"
                        className="h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-1.5">
                  <GitMerge className="w-4 h-4" /> Gabung Brand
                </button>
              )}
              <button onClick={() => setShowImport(true)} data-testid="inv-prod-import"
                      className="h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-1.5">
                <Upload className="w-4 h-4" /> Import
              </button>
              <button onClick={() => setShowBulk(true)} data-testid="inv-prod-bulk"
                      title="Ubah harga jual massal per brand"
                      className="h-10 px-3 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-sm font-medium text-amber-800 inline-flex items-center gap-1.5">
                <Tags className="w-4 h-4" /> Bulk Harga
              </button>
              <button onClick={() => setShowUsulanMinStock(true)} data-testid="inv-prod-usulan-minstock"
                      title="Usulan stok minimum otomatis: rata-rata penjualan harian x lead time supplier x 1,5"
                      className="h-10 px-3 rounded-lg border border-sky-300 bg-sky-50 hover:bg-sky-100 text-sm font-medium text-sky-800 inline-flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4" /> Usulan Min. Stok
              </button>
              <button onClick={() => { setHbeliTampil(true); setForm({ ...kosong }); }} data-testid="inv-prod-add"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
                <Plus className="w-4 h-4" /> Produk Baru
              </button>
            </>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        {/* Filter & sort (poin 14). Kontrol filternya komponen BERSAMA dengan
            Price List (brief 22 Sep 2026: "tab filter sama persis") — yang
            khas halaman ini (urutan + jumlah) masuk sebagai children. */}
        <FilterProduk className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap"
                      nilai={filter} ubah={ubahFilter} opsi={opsiFilter}
                      cari={{
                        nilai: qInput,
                        onChange: v => { setPage(1); setQInput(v); },
                        onEnter: () => { setPage(1); setQApplied(qInput); },
                      }}
                      gudang={gudang} suppliers={suppliers}
                      gudangGagal={gudangGagal} supplierGagal={supplierGagal}
                      agingHari={meta.aging_hari} testid={TESTID_MASTER_PRODUK}>
          <select value={`${filter.sort}:${filter.arah}`}
                  onChange={e => { const [s, a] = e.target.value.split(":"); ubahFilter({ sort: s, arah: a }); }}
                  data-testid="inv-prod-sort" aria-label="Urutkan daftar produk"
                  className="h-9 px-2 border border-slate-200 rounded-lg text-sm">
            <option value="nama:asc">Urut: Nama A-Z</option>
            <option value="nama:desc">Urut: Nama Z-A</option>
            <option value="brand:asc">Urut: Brand</option>
            <option value="kategori:asc">Urut: Kategori</option>
            <option value="umur:desc">Urut: Paling lama nangkring</option>
            <option value="stok:desc">Urut: Stok terbanyak</option>
            <option value="harga_jual:desc">Urut: Harga tertinggi</option>
            <option value="harga_jual:asc">Urut: Harga terendah</option>
          </select>
          <div className="flex-1" />
          <p className="text-xs text-slate-500">{meta.total} produk</p>
        </FilterProduk>

        {/* Catatan jujur dari server soal akses modal & margin (batch P) —
            SATU baris teks, beda dari `catatan` (daftar) Price List. */}
        {catatanAkses && (
          <p className="px-4 py-2 text-xs text-slate-500 bg-slate-50 border-b border-slate-100"
             data-testid="inv-prod-catatan-akses">
            {catatanAkses}
          </p>
        )}

        <TabelDesktop>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr className="text-left">
                <th className="px-3 py-2.5 w-8"></th>
                <th className="px-3 py-2.5 font-semibold">SKU / Barcode</th>
                <th className="px-3 py-2.5 font-semibold">Nama</th>
                <th className="px-3 py-2.5 font-semibold">Kategori</th>
                <th className="px-3 py-2.5 font-semibold text-right">Stok{filter.warehouse_id && <span className="ml-1 font-normal normal-case text-[10px] text-emerald-700" data-testid="inv-prod-th-stok-gudang">(gudang terpilih)</span>}</th>
                <th className="px-3 py-2.5 font-semibold text-right">Umur</th>
                {modalTerlihat && <th className="px-3 py-2.5 font-semibold text-right" data-testid="inv-prod-th-modal">Modal/unit</th>}
                <th className="px-3 py-2.5 font-semibold text-right">Harga Jual</th>
                {marginBentuk && <th className="px-3 py-2.5 font-semibold text-right" data-testid="inv-prod-th-margin">Margin</th>}
                <th className="px-3 py-2.5 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <LoadingState
                loading={loading}
                kosong={!loading && items.length === 0}
                pesanKosong='Belum ada produk. Mulai dengan "Produk Baru", "Import", atau scan barcode barang.'
                varian="tabel"
                kolom={8 + (modalTerlihat ? 1 : 0) + (marginBentuk ? 1 : 0)}
                data-testid="inv-prod-loading"
              />
              {!loading && items.map(p => (
                <tr key={p.sku} data-testid={`inv-prod-${p.sku}`}
                    className={`hover:bg-slate-50/60 ${p.is_active === false ? "opacity-60" : ""}`}>
                  <td className="px-3 py-2.5">
                    <input type="checkbox" checked={pilih.includes(p.sku)} onChange={() => toggle(p.sku)}
                           data-testid={`inv-prod-pick-${p.sku}`} aria-label={`Pilih ${p.sku} untuk cetak label`}
                           className="w-4 h-4 rounded border-slate-300" />
                  </td>
                  <td className="px-3 py-2.5">
                    <p className="font-mono text-xs font-semibold text-slate-900">{p.sku}</p>
                    {p.barcode ? (
                      <p className="font-mono text-[10px] text-slate-500">{p.barcode}</p>
                    ) : can("inventory_produk", "create") && (
                      <button onClick={() => generateBarcode(p)} data-testid={`inv-prod-gen-${p.sku}`}
                              title="Buat barcode internal otomatis (EAN-13)"
                              className="text-[10px] text-emerald-700 hover:underline inline-flex items-center gap-0.5">
                        <Wand2 className="w-3 h-3" /> buat barcode
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {/* P5 (brief 4 Sep): klik nama -> total qty terbagi di gudang
                        mana saja. Rinciannya dari GET /inventory/products/{sku},
                        yang sudah menerapkan lingkup cabang pengguna. */}
                    <button type="button" onClick={() => setRincianFor(p)}
                            data-testid={`inv-prod-rincian-${p.sku}`}
                            title="Lihat sebaran stok per gudang"
                            className="text-left text-slate-800 hover:text-emerald-700 hover:underline">
                      {p.nama}
                    </button>
                    <p className="text-[11px] text-slate-500">
                      {[p.brand, p.sub_kategori, p.satuan].filter(Boolean).join(" · ")}
                    </p>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="text-[11px] px-2 py-0.5 rounded border bg-slate-50 text-slate-700 border-slate-200">
                      {kategoriFlatOptions.find(k => k.v === p.kategori)?.l || p.kategori}
                    </span>
                    {p.has_imei && <span className="ml-1 text-[10px] text-emerald-700">IMEI</span>}
                    {p.is_active === false && (
                      <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">Nonaktif</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <span className={p.low_stock ? "text-rose-700 font-semibold" : "text-slate-900"}>
                      {p.stock_qty}
                    </span>
                    {p.low_stock && <AlertTriangle className="w-3 h-3 text-rose-600 inline ml-1" />}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {p.umur_hari == null ? (
                      <span className="text-slate-300">—</span>
                    ) : (
                      <span className={p.aging ? "text-rose-700 font-semibold" : "text-slate-600"}
                            title={p.aging ? `Lewat ambang ${meta.aging_hari} hari` : ""}>
                        {p.umur_hari} hr
                      </span>
                    )}
                  </td>
                  {modalTerlihat && <td className="px-3 py-2.5 text-right"><ModalUnitCell p={p} /></td>}
                  <td className="px-3 py-2.5 text-right">
                    {p.peringatan_harga_jual ? (
                      <span data-testid={`inv-prod-peringatan-harga-${p.sku}`}
                            title="Produk aktif dengan harga jual Rp 0 — kalau terjual, seluruh nilainya terbaca sebagai laba. Isi harga jualnya sekarang."
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-rose-600 text-white text-[11px] font-bold uppercase tracking-wide animate-pulse">
                        <AlertTriangle className="w-3.5 h-3.5" /> Harga jual belum diisi!
                      </span>
                    ) : (
                      <span className="font-mono text-slate-900">{idr(p.harga_jual)}</span>
                    )}
                  </td>
                  {marginBentuk && (
                    <td className="px-3 py-2.5 text-right">
                      <MarginBadge p={p} bentuk={marginBentuk} />
                    </td>
                  )}
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setLabelFor({ skus: [p.sku] })} title="Cetak label barcode"
                              data-testid={`inv-prod-label-${p.sku}`}
                              className="w-8 h-8 rounded hover:bg-slate-100 text-slate-600 grid place-items-center">
                        <Barcode className="w-4 h-4" />
                      </button>
                      {can("inventory_produk", "update") && (
                        <button onClick={() => bukaEdit(p)} data-testid={`inv-prod-edit-${p.sku}`}
                                className="px-2 h-8 rounded hover:bg-slate-100 text-slate-600 text-xs">Ubah</button>
                      )}
                      {can("inventory_produk", "update") && (
                        <button onClick={() => toggleAktif(p)} data-testid={`prod-toggle-aktif-${p.sku}`}
                                title={p.is_active === false ? "Aktifkan kembali produk ini" : "Nonaktifkan produk ini (ditolak kalau masih berstok)"}
                                className={`px-2 h-8 rounded text-xs font-medium inline-flex items-center gap-1 ${
                                  p.is_active === false
                                    ? "text-emerald-700 hover:bg-emerald-50"
                                    : "text-slate-600 hover:bg-rose-50 hover:text-rose-600"}`}>
                          {p.is_active === false
                            ? <><Power className="w-3.5 h-3.5" /> Aktifkan</>
                            : <><PowerOff className="w-3.5 h-3.5" /> Nonaktifkan</>}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TabelDesktop>

        {/* Kartu mobile (brief owner 17 Agu #16) — SATU sumber data (`items`)
            dengan tabel di atas, dipisah lewat CSS (`md:hidden` di
            `DaftarKartuMobile`), bukan render kondisional JS. Ringkas: nama +
            stok + harga jual + aksi tampil langsung di kartu; sisanya
            (kategori, umur, modal per unit, margin) jadi baris detail sekunder.
            Pilih-massal untuk cetak label BUKAN dibawa ke sini secara sengaja
            — mencentang banyak SKU sambil scroll di layar kecil praktiknya
            dikerjakan dari desktop; aksi cetak label PER PRODUK tetap ada. */}
        <DaftarKartuMobile testid="inv-prod-kartu">
          <LoadingState
            loading={loading}
            kosong={!loading && items.length === 0}
            pesanKosong='Belum ada produk. Mulai dengan "Produk Baru", "Import", atau scan barcode barang.'
            varian="kartu"
            data-testid="inv-prod-loading-m"
          />
          {!loading && items.map(p => (
            <KartuBaris key={p.sku} testid={`inv-prod-card-${p.sku}`}
                        className={p.is_active === false ? "opacity-60" : ""}>
              <KartuBarisHeader
                kiri={<>
                  <p className="font-mono text-[11px] text-slate-500">
                    {p.sku}{p.barcode ? ` · ${p.barcode}` : ""}
                  </p>
                  <p className="text-sm font-semibold text-slate-900">{p.nama}</p>
                  {p.is_active === false && (
                    <span className="inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">Nonaktif</span>
                  )}
                </>}
                kanan={p.peringatan_harga_jual ? (
                  <span data-testid={`inv-prod-peringatan-harga-${p.sku}-m`}
                        title="Produk aktif dengan harga jual Rp 0 — kalau terjual, seluruh nilainya terbaca sebagai laba. Isi harga jualnya sekarang."
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-rose-600 text-white text-[10px] font-bold uppercase tracking-wide">
                    <AlertTriangle className="w-3 h-3" /> Belum diisi!
                  </span>
                ) : (
                  <span className="font-mono text-sm font-semibold text-slate-900">{idr(p.harga_jual)}</span>
                )}
              />
              <KartuBarisMeta label="Stok" tebal value={
                <span className={p.low_stock ? "text-rose-700" : ""}>
                  {p.stock_qty}{p.low_stock && <AlertTriangle className="w-3 h-3 text-rose-600 inline ml-1" />}
                </span>
              } />
              <KartuBarisMeta label="Kategori" value={<>
                <span className="text-[11px] px-2 py-0.5 rounded border bg-slate-50 text-slate-700 border-slate-200">
                  {kategoriFlatOptions.find(k => k.v === p.kategori)?.l || p.kategori}
                </span>
                {p.has_imei && <span className="ml-1 text-[10px] text-emerald-700">IMEI</span>}
              </>} />
              <KartuBarisMeta label="Brand / Satuan" value={[p.brand, p.sub_kategori, p.satuan].filter(Boolean).join(" · ")} />
              <KartuBarisMeta label="Umur" value={p.umur_hari == null ? null : (
                <span className={p.aging ? "text-rose-700 font-semibold" : ""}
                      title={p.aging ? `Lewat ambang ${meta.aging_hari} hari` : ""}>
                  {p.umur_hari} hr
                </span>
              )} />
              {modalTerlihat && <KartuBarisMeta label="Modal/unit" value={<ModalUnitCell p={p} suffix="-m" />} />}
              {marginBentuk && <KartuBarisMeta label="Margin" value={<MarginBadge p={p} suffix="-m" bentuk={marginBentuk} />} />}
              <KartuBarisAksi>
                {!p.barcode && can("inventory_produk", "create") && (
                  <button onClick={() => generateBarcode(p)} data-testid={`inv-prod-gen-${p.sku}-m`}
                          className="px-2.5 h-8 rounded hover:bg-slate-100 text-emerald-700 text-xs font-medium inline-flex items-center gap-1">
                    <Wand2 className="w-3.5 h-3.5" /> Barcode
                  </button>
                )}
                <button onClick={() => setLabelFor({ skus: [p.sku] })} data-testid={`inv-prod-label-${p.sku}-m`}
                        className="px-2.5 h-8 rounded hover:bg-slate-100 text-slate-600 text-xs font-medium inline-flex items-center gap-1">
                  <Barcode className="w-3.5 h-3.5" /> Label
                </button>
                {can("inventory_produk", "update") && (
                  <button onClick={() => bukaEdit(p)} data-testid={`inv-prod-edit-${p.sku}-m`}
                          className="px-2.5 h-8 rounded hover:bg-slate-100 text-slate-600 text-xs font-medium">Ubah</button>
                )}
                {can("inventory_produk", "update") && (
                  <button onClick={() => toggleAktif(p)} data-testid={`prod-toggle-aktif-${p.sku}-m`}
                          title={p.is_active === false ? "Aktifkan kembali produk ini" : "Nonaktifkan produk ini (ditolak kalau masih berstok)"}
                          className={`px-2.5 h-8 rounded text-xs font-medium inline-flex items-center gap-1 ${
                            p.is_active === false
                              ? "text-emerald-700 hover:bg-emerald-50"
                              : "text-slate-600 hover:bg-rose-50 hover:text-rose-600"}`}>
                    {p.is_active === false
                      ? <><Power className="w-3.5 h-3.5" /> Aktifkan</>
                      : <><PowerOff className="w-3.5 h-3.5" /> Nonaktifkan</>}
                  </button>
                )}
              </KartuBarisAksi>
            </KartuBaris>
          ))}
        </DaftarKartuMobile>

        {meta.total_pages > 1 && (
          <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between text-sm">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="inv-prod-prev"
                    className="px-3 h-8 rounded border border-slate-200 disabled:opacity-40">Sebelumnya</button>
            <span className="text-slate-500">Halaman {meta.page} dari {meta.total_pages}</span>
            <button disabled={page >= meta.total_pages} onClick={() => setPage(p => p + 1)} data-testid="inv-prod-next"
                    className="px-3 h-8 rounded border border-slate-200 disabled:opacity-40">Berikutnya</button>
          </div>
        )}
      </div>

      {rincianFor && <RincianGudangModal produk={rincianFor} gudangTerpilih={filter.warehouse_id}

                                         onClose={() => setRincianFor(null)} />}

      {labelFor && (
        <LabelModal skus={labelFor.skus} onClose={() => setLabelFor(null)} onCetak={cetakLabel} />
      )}
      {showPengaturan && (
        <PengaturanModal opsi={opsi} onClose={(berubah) => {
          setShowPengaturan(false);
          if (berubah) { refetchOpsi(); invalidateProduk(); }
        }} />
      )}
      {showImport && (
        <ImportModal onClose={(berubah) => { setShowImport(false); if (berubah) invalidateProduk(); }} />
      )}
      {showBulk && (
        <BulkHargaModal brands={brandFilterList} onClose={(berubah) => {
          setShowBulk(false);
          if (berubah) invalidateProduk();
        }} />
      )}
      {showUsulanMinStock && (
        <UsulanMinStockModal brands={brandFilterList} onClose={(berubah) => {
          setShowUsulanMinStock(false);
          if (berubah) invalidateProduk();
        }} />
      )}

      {/* Modal Merge Brand — DUA TAHAP seperti Gabung Supplier: pratinjau
          dulu, tombol eksekusi baru aktif setelah pratinjau bilang aman
          (tidak ada tabrakan DAN belum pernah jadi sumber merge). Ubah
          sumber/target APA PUN membatalkan pratinjau (`ubahMergeBrand`). */}
      {mergeBrand && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
             onClick={() => !mergeBrand.executing && setMergeBrand(null)}>
          <div onClick={e => e.stopPropagation()} data-testid="inv-merge-brand-modal"
               className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <p className="font-display font-bold text-base text-slate-900 flex items-center gap-2">
                <GitMerge className="w-4 h-4 text-emerald-600" /> Gabung Brand
              </p>
              <button onClick={() => setMergeBrand(null)} data-testid="inv-merge-brand-close"
                      className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <p className="text-xs text-slate-500">
                Semua dokumen berejaan <b>sumber</b> (Master Produk, unit ber-IMEI, kartu stok, opname,
                Blind Opname, Cashback) ditulis ulang jadi ejaan <b>target</b>. Brand tidak punya id —
                teksnya ADALAH kuncinya, jadi pencocokan sumber case-insensitive (mis. "Apple" mencakup
                "APPLE"/"aPPle").
              </p>

              <div>
                <label htmlFor="inv-merge-brand-source" className="text-xs font-medium text-slate-600">
                  Brand sumber (ejaan lama)
                </label>
                <input id="inv-merge-brand-source" value={mergeBrand.sourceNama} data-testid="inv-merge-brand-source"
                       list="inv-merge-brand-daftar" placeholder="cth. Apple"
                       onChange={e => ubahMergeBrand({ sourceNama: e.target.value })}
                       className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label htmlFor="inv-merge-brand-target" className="text-xs font-medium text-slate-600">
                  Brand target (ejaan yang dipakai seterusnya)
                </label>
                <input id="inv-merge-brand-target" value={mergeBrand.targetNama} data-testid="inv-merge-brand-target"
                       list="inv-merge-brand-daftar" placeholder="cth. APPLE"
                       onChange={e => ubahMergeBrand({ targetNama: e.target.value })}
                       className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <datalist id="inv-merge-brand-daftar">
                {opsi.brand.map(b => <option key={b} value={b} />)}
              </datalist>

              <button onClick={previewMergeBrand} disabled={mergeBrand.previewLoading} data-testid="inv-merge-brand-preview-btn"
                      className="h-10 px-4 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-sm font-medium w-full disabled:opacity-50">
                {mergeBrand.previewLoading ? "Memuat pratinjau..." : "Lihat Pratinjau"}
              </button>

              {/* Hasil pratinjau — TIDAK menulis apa pun ke database */}
              {mergeBrand.preview && (
                <div className="border border-slate-200 rounded-xl p-3 space-y-3" data-testid="inv-merge-brand-preview-result">
                  <div>
                    <p className="text-[11px] font-semibold text-slate-500 uppercase mb-1">
                      Dokumen yang akan dipindahkan ({mergeBrand.preview.total_dokumen})
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {mergeBrand.preview.dampak.filter(d => d.jumlah > 0).map(d => (
                        <span key={d.koleksi} className="text-[11px] px-2 py-1 rounded-full bg-slate-100 text-slate-700">
                          {d.koleksi}: {d.jumlah}
                        </span>
                      ))}
                      {mergeBrand.preview.total_dokumen === 0 && (
                        <span className="text-[11px] text-slate-400">Tidak ada dokumen yang tersentuh.</span>
                      )}
                    </div>
                  </div>

                  {mergeBrand.preview.sudah_pernah_merge ? (
                    <div className="bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="inv-merge-brand-rantai">
                      <p className="text-xs font-semibold text-rose-800 flex items-center gap-1.5">
                        <XCircle className="w-4 h-4" /> Sudah pernah digabung — eksekusi diblokir
                      </p>
                      <p className="text-[11px] text-rose-700 mt-1">{mergeBrand.preview.sudah_pernah_merge}</p>
                    </div>
                  ) : mergeBrand.preview.tabrakan.length > 0 ? (
                    <div className="bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="inv-merge-brand-tabrakan">
                      <p className="text-xs font-semibold text-rose-800 flex items-center gap-1.5 mb-1.5">
                        <XCircle className="w-4 h-4" /> {mergeBrand.preview.tabrakan.length} tabrakan — eksekusi diblokir
                      </p>
                      <ul className="space-y-1">
                        {mergeBrand.preview.tabrakan.map((t, i) => (
                          <li key={i} className="text-[11px] text-rose-700">{t.pesan}</li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 flex items-center gap-1.5 text-xs text-emerald-800">
                      <CheckCircle2 className="w-4 h-4" /> Tidak ada tabrakan — siap digabung.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
              <button onClick={() => setMergeBrand(null)} disabled={mergeBrand.executing}
                      className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                Batal
              </button>
              <button onClick={eksekusiMergeBrand} data-testid="inv-merge-brand-execute-btn"
                      disabled={!mergeBrand.preview?.bisa_eksekusi || mergeBrand.executing}
                      title={!mergeBrand.preview ? "Lihat pratinjau dulu" : undefined}
                      className="h-10 px-4 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium disabled:opacity-50 disabled:bg-slate-300">
                {mergeBrand.executing ? "Menggabungkan..." : "Eksekusi Gabung"}
              </button>
            </div>
          </div>
        </div>
      )}

      {form && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setForm(null)}>
          <div onClick={e => e.stopPropagation()} data-testid="inv-prod-modal"
               className="bg-white rounded-2xl max-w-lg w-full max-h-[88vh] flex flex-col shadow-2xl">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-display font-bold text-lg flex items-center gap-2">
                <Package className="w-5 h-5 text-emerald-600" /> {form.id ? "Ubah Produk" : "Produk Baru"}
              </h3>
              <button onClick={() => setForm(null)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto flex-1 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <F label="SKU" htmlFor="inv-f-sku"
                   hint={form.id
                     ? "Kode internal — terkunci, mengganti SKU memutus riwayat produk lama."
                     : gadgetTerpilihForm
                       ? "Kosongkan untuk dibuat otomatis dari Brand+Type+Ram/Storage+Warna, atau isi sendiri."
                       : "Kode internal, tidak boleh sama."}>
                  <input id="inv-f-sku" value={form.sku} disabled={!!form.id} data-testid="inv-f-sku"
                         onChange={e => setForm(f => ({ ...f, sku: e.target.value.toUpperCase() }))}
                         className={`${inp} disabled:bg-slate-100`} />
                </F>
                <F label="Barcode pabrik (UPC/EAN)" htmlFor="inv-f-barcode" hint="Kosongkan lalu pakai 'buat barcode' kalau tidak ada">
                  <input id="inv-f-barcode" value={form.barcode} data-testid="inv-f-barcode"
                         onChange={e => setForm(f => ({ ...f, barcode: e.target.value }))} className={inp} />
                </F>
              </div>

              {/* ============ Hierarki Kategori (poin 2-4 brief bos 23 Agu 2026)
                  ============ Kategori Utama menentukan SELURUH bentuk form di
                  bawahnya (status New/Second wajib untuk 4 kategori gadget,
                  daftar brand yang boleh dipilih, dan apakah Type/Ram-Storage/
                  Warna/Distributor muncul sama sekali). Mengganti pilihan ini
                  MEMBERSIHKAN semua turunan yang jadi tidak sah — staf tidak
                  boleh sampai ke titik "Second tapi kategorinya Accessories"
                  (aturan #3 CLAUDE.md: jangan tampilkan yang ujungnya ditolak). */}
              <F label="Kategori" htmlFor="inv-f-kategori-utama"
                 hint={gadgetTerpilihForm
                   ? "Wajib pilih New/Second di bawah — ikut menentukan akun persediaan."
                   : "Kategori ini tidak punya sub-kategori — SKU & Nama diisi manual."}>
                <select id="inv-f-kategori-utama" value={form.kategori_utama} data-testid="inv-f-kategori-utama"
                        onChange={e => ubahKategoriUtama(e.target.value)} className={inp}>
                  {kategoriUtamaList.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
                </select>
              </F>

              {gadgetTerpilihForm && (
                <div className="grid grid-cols-2 gap-3">
                  <F label="Status" htmlFor="inv-f-status-kondisi" hint="Wajib dipilih sebelum bisa disimpan.">
                    <select id="inv-f-status-kondisi" value={form.status_kondisi} data-testid="inv-f-status-kondisi"
                            onChange={e => {
                              const baru = e.target.value;
                              setForm(f => ({ ...f, status_kondisi: baru, varian_second: baru === "second" ? f.varian_second : "" }));
                            }} className={inp}>
                      <option value="">— pilih —</option>
                      <option value="baru">New</option>
                      <option value="second">Second</option>
                    </select>
                  </F>
                  {form.status_kondisi === "second" && (
                    <F label="Kondisi unit second" htmlFor="inv-f-varian-second">
                      <select id="inv-f-varian-second" value={form.varian_second} data-testid="inv-f-varian-second"
                              onChange={e => setForm(f => ({ ...f, varian_second: e.target.value }))} className={inp}>
                        <option value="">— pilih kondisi —</option>
                        {varianSecondList.map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </F>
                  )}
                </div>
              )}
              {form.status_kondisi === "second" && !form.varian_second && (
                // Poin 3 — 91 produk second lama variannya kosong; backend
                // mewajibkannya saat disentuh. Permintaan JELAS, bukan toast
                // 400 mentah (kalimat PERSIS diminta brief).
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5"
                   data-testid="inv-f-varian-second-hint">
                  Kondisi unit second ini belum tercatat — pilih dulu.
                </p>
              )}

              <div className="grid grid-cols-2 gap-3">
                {/* Brand WAJIB untuk produk BARU (keputusan owner D2, brief 21
                    Agu — backend menolak `POST /products` tanpa brand kalau
                    SKU-nya belum ada, lihat `products.py::_upsert_data`).
                    Produk LAMA yang sudah telanjur tidak punya brand tetap
                    harus bisa diedit tanpa dipaksa mengisinya — `required`
                    di sini SENGAJA hanya aktif saat `!form.id` (mode "Produk
                    Baru"), bukan saat mengedit produk yang sudah ada, supaya
                    perilaku form persis mengikuti aturan backend. Daftar yang
                    ditawarkan (poin 1/6) mengikuti Kategori Utama terpilih —
                    `brandListForm` (turunan `daftarBrandUntukKategoriUtama`). */}
                <F label={form.id ? "Brand" : "Brand *"} htmlFor="inv-f-brand"
                   hint={(brandListForm.length ? "Hanya brand terdaftar untuk kategori ini" : "Daftarkan brand di Pengaturan agar jadi dropdown")
                         + (form.id ? "" : " — wajib untuk produk baru")}>
                  {brandListForm.length > 0 ? (
                    <select id="inv-f-brand" value={form.brand || ""} data-testid="inv-f-brand"
                            required={!form.id}
                            onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} className={inp}>
                      <option value="">— pilih brand —</option>
                      {brandListForm.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  ) : (
                    <input id="inv-f-brand" value={form.brand || ""} data-testid="inv-f-brand"
                           required={!form.id}
                           onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} className={inp} />
                  )}
                </F>
                <F label="Satuan" htmlFor="inv-f-satuan">
                  <select id="inv-f-satuan" value={form.satuan} data-testid="inv-f-satuan"
                          onChange={e => setForm(f => ({ ...f, satuan: e.target.value }))} className={inp}>
                    {(opsi.satuan.length ? opsi.satuan : ["pcs"]).map(s => <option key={s} value={s}>{s}</option>)}
                    {form.satuan && !opsi.satuan.includes(form.satuan) && (
                      <option value={form.satuan}>{form.satuan} (lama)</option>
                    )}
                  </select>
                </F>
              </div>

              {/* ============ Poin 5 — bahan Nama & SKU otomatis, HANYA
                  kategori gadget (Handphone/Tab/Laptop/Wearable). Original
                  Acc/Accessories/SIM Sticker tidak pernah melihat kolom ini. */}
              {gadgetTerpilihForm && form.id && (!form.tipe || !form.ram_storage || !form.warna) && (
                // Poin 5 — 564 produk lama namanya TIDAK dibentuk ulang
                // (keputusan owner); membuka salah satu untuk diedit akan
                // punya kolom ini KOSONG. Penjelasan jelas, bukan error
                // mentah begitu staf menekan Simpan.
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-[11px] text-amber-800"
                     data-testid="inv-f-legacy-gadget-notice">
                  Produk lama ini belum punya Type/Ram-Storage/Warna tercatat (dibuat sebelum penamaan
                  otomatis berlaku) — isi ketiganya di bawah supaya Nama & SKU bisa dibentuk otomatis saat disimpan.
                </div>
              )}
              {/* ============ P1/P2 (brief owner 12 Sep 2026) — DUA kolom
                  bersebelahan: bahan Nama produk dan bahan SKU produk, masing
                  masing ENAM sub-kolom (Kategori, Brand, Tipe, Storage, Warna,
                  Distributor).

                  Kategori & Brand tampil sebagai PILIHAN, bukan ketikan bebas
                  (keputusan owner 12 Sep): keduanya punya registri, dan
                  membiarkannya diketik akan melahirkan "Apple"/"APPLE"/"aple"
                  sebagai tiga merek berbeda di laporan. Keduanya terikat ke
                  state yang SAMA dengan pilihan di bagian atas form — mengubah
                  di sini mengubah di sana, bukan dua nilai yang bisa berbeda.

                  Kolom SKU terisi otomatis dari kolom Nama (lewat singkatan
                  master kalau ada, kalau tidak dari nilainya sendiri) dan
                  boleh ditimpa per produk. ============ */}
              {gadgetTerpilihForm && (
                <div className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50/60"
                     data-testid="inv-f-bahan-blok">
                  <div className="grid grid-cols-2 gap-3">
                    <p className="text-xs font-semibold text-slate-700">Bahan Nama produk otomatis</p>
                    <p className="text-xs font-semibold text-slate-700">Bahan SKU produk otomatis</p>
                  </div>
                  {DIMENSI_BAHAN.map(d => {
                    const bahan = bahanSkuEfektif(opsi, form);
                    return (
                      <div key={d.key} className="grid grid-cols-2 gap-3">
                        <F label={d.label} htmlFor={`inv-f-${d.key}`} hint={d.hint}>
                          {d.key === "kategori" ? (
                            <select id="inv-f-kategori" value={form.kategori_utama || ""}
                                    data-testid="inv-f-bahan-kategori"
                                    onChange={e => ubahKategoriUtama(e.target.value)}
                                    className={inp}>
                              <option value="">— pilih —</option>
                              {kategoriUtamaList.map(k => (
                                <option key={k.key} value={k.key}>{k.label}</option>
                              ))}
                            </select>
                          ) : d.key === "brand" ? (
                            <select id="inv-f-brand-bahan" value={form.brand || ""}
                                    data-testid="inv-f-bahan-brand"
                                    onChange={e => setForm(f => ({ ...f, brand: e.target.value }))}
                                    className={inp}>
                              <option value="">— pilih —</option>
                              {brandListForm.map(b => <option key={b} value={b}>{b}</option>)}
                            </select>
                          ) : (
                            <input id={`inv-f-${d.key}`} value={form[d.key] || ""}
                                   data-testid={`inv-f-bahan-${d.key}`}
                                   onChange={e => setForm(f => ({ ...f, [d.key]: e.target.value }))}
                                   className={inp} />
                          )}
                        </F>
                        <F label={d.label} htmlFor={`inv-f-sku-${d.key}`}
                           hint={form[`sku_${d.key}`] ? "diketik sendiri" : "otomatis"}>
                          <input id={`inv-f-sku-${d.key}`} value={form[`sku_${d.key}`] || ""}
                                 data-testid={`inv-f-sku-${d.key}`}
                                 placeholder={bahan[d.key] || "—"}
                                 onChange={e => setForm(f => ({
                                   ...f, [`sku_${d.key}`]: e.target.value.toUpperCase(),
                                 }))}
                                 className={`${inp} font-mono uppercase`} />
                        </F>
                      </div>
                    );
                  })}
                  <p className="text-[11px] text-slate-500">
                    Kolom SKU terisi otomatis dari kolom Nama di sebelahnya. Isi sendiri kalau ingin
                    singkatan yang berbeda untuk produk ini.
                  </p>
                </div>
              )}

              {/* Pratinjau SKU (poin 1) — SKU punya kolom pembangunnya SENDIRI,
                  dipisah dari nama: nama memakai nilai penuh, SKU memakai kode
                  pendek tiap nilai. Ditampilkan SEBELUM disimpan supaya staf
                  tahu apa yang akan terbentuk, dan supaya nilai yang kodenya
                  belum didaftarkan owner ketahuan di sini — bukan lewat toast
                  400 setelah semua field terlanjur diisi. */}
              {skuAkanDirakit && skuPreviewForm.aktif && (
                skuPreviewForm.kurang.length ? (
                  <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5"
                       data-testid="inv-f-sku-preview-kurang">
                    Kode SKU belum terdaftar untuk {skuPreviewForm.kurang.join("; ")}. Daftarkan dulu
                    lewat Pengaturan &gt; Kode SKU — kode sengaja diatur sekali di master supaya satu
                    nilai selalu dapat singkatan yang sama.
                  </div>
                ) : (
                  <div className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5"
                       data-testid="inv-f-sku-preview">
                    SKU yang akan terbentuk: <b className="font-mono text-slate-800">{skuPreviewForm.sku}</b>
                  </div>
                )
              )}

              <F label={gadgetTerpilihForm ? "Nama produk (pratinjau otomatis)" : "Nama produk"} htmlFor="inv-f-nama"
                 hint={gadgetTerpilihForm
                   ? "Dibentuk otomatis dari Brand+Type+Ram/Storage+Warna(+Distributor) — tidak bisa diketik manual."
                   : undefined}>
                <input id="inv-f-nama" value={gadgetTerpilihForm ? namaPreviewForm : form.nama} data-testid="inv-f-nama"
                       disabled={gadgetTerpilihForm}
                       placeholder={gadgetTerpilihForm ? "Isi Brand/Type/Ram-Storage/Warna dulu di atas" : undefined}
                       onChange={e => setForm(f => ({ ...f, nama: e.target.value }))}
                       className={`${inp} disabled:bg-slate-100`} />
              </F>

              {/* Kategori custom (poin 10-11) — pengelompokan bebas TAMBAHAN,
                  bukan pengganti hierarki di atas; berlaku untuk semua
                  Kategori Utama. */}
              <F label="Kategori custom" htmlFor="inv-f-subkategori" hint="Atur daftarnya di Pengaturan">
                {opsi.kategori_custom.length > 0 ? (
                  <select id="inv-f-subkategori" value={form.sub_kategori || ""} data-testid="inv-f-subkategori"
                          onChange={e => setForm(f => ({ ...f, sub_kategori: e.target.value }))} className={inp}>
                    <option value="">— tanpa —</option>
                    {opsi.kategori_custom.map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                ) : (
                  <input id="inv-f-subkategori" value={form.sub_kategori || ""} data-testid="inv-f-subkategori" placeholder="bebas (daftar belum diatur)"
                         onChange={e => setForm(f => ({ ...f, sub_kategori: e.target.value }))} className={inp} />
                )}
              </F>
              {/* Rasio konversi satuan besar->kecil (brief bos #25 / batch D #1a):
                  opsional, tapi kalau salah satu diisi keduanya wajib — backend
                  menegakkan ini, di sini sekadar keterangan supaya jelas dari awal. */}
              <div className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50/60">
                <p className="text-xs font-semibold text-slate-700">Satuan besar (opsional)</p>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Satuan besar" htmlFor="prod-satuan-besar">
                    <select id="prod-satuan-besar" value={form.satuan_besar || ""} data-testid="prod-satuan-besar"
                            onChange={e => setForm(f => ({ ...f, satuan_besar: e.target.value }))} className={inp}>
                      <option value="">— tanpa —</option>
                      {(opsi.satuan.length ? opsi.satuan : ["pcs"])
                        .filter(s => s !== form.satuan)
                        .map(s => <option key={s} value={s}>{s}</option>)}
                      {form.satuan_besar && !opsi.satuan.includes(form.satuan_besar) && (
                        <option value={form.satuan_besar}>{form.satuan_besar} (lama)</option>
                      )}
                    </select>
                  </F>
                  <F label="Isi per satuan besar" htmlFor="prod-isi-besar">
                    <input id="prod-isi-besar" type="number" min="2" value={form.isi_per_satuan_besar}
                           data-testid="prod-isi-besar" placeholder="mis. 12"
                           onChange={e => setForm(f => ({ ...f, isi_per_satuan_besar: e.target.value }))} className={inp} />
                  </F>
                </div>
                <p className="text-[11px] text-slate-500">
                  Mis. 1 dus = 12 pcs; stok selalu tersimpan dalam satuan kecil.
                </p>
              </div>
              <label className="flex items-start gap-2 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3">
                <input type="checkbox" checked={form.has_imei} data-testid="inv-f-imei"
                       onChange={e => setForm(f => ({ ...f, has_imei: e.target.checked, costing_method: e.target.checked ? "actual" : f.costing_method === "actual" ? "" : f.costing_method }))}
                       className="w-4 h-4 rounded border-slate-300 mt-0.5" />
                <span>
                  Barang ini punya IMEI / serial number
                  <span className="block text-[11px] text-slate-500 mt-0.5">
                    Setiap unit dicatat sendiri dan modalnya melekat pada unit itu, jadi laba per unit persis.
                    Wajib untuk HP. Bentuk IMEI/SN bebas — tidak harus 15 digit.
                  </span>
                </span>
              </label>
              {/* Poin A.7 (SIM): metode modal harus BISA DIPILIH — dulu backend sudah
                  mendukung actual/fifo/average tapi UI tidak menyediakannya, jadi FIFO
                  tidak pernah terjangkau dari aplikasi. Ber-IMEI = terkunci Actual
                  (aturan backend: modal wajib melekat per unit). */}
              <F label="Metode perhitungan modal (HPP)" htmlFor="inv-f-costing"
                 hint={form.has_imei
                   ? "Terkunci Actual Cost — barang ber-IMEI modalnya melekat per unit"
                   : "Tidak bisa diubah lagi selama produk masih punya sisa stok"}>
                <select id="inv-f-costing" value={form.has_imei ? "actual" : (form.costing_method || "")}
                        disabled={form.has_imei} data-testid="inv-f-costing"
                        onChange={e => setForm(f => ({ ...f, costing_method: e.target.value }))}
                        className={inp + (form.has_imei ? " opacity-60 cursor-not-allowed" : "")}>
                  {form.has_imei
                    ? <option value="actual">Actual Cost — modal per unit (IMEI)</option>
                    : <>
                        <option value="">Otomatis (Average)</option>
                        <option value="average">Average — modal rata-rata tertimbang</option>
                        <option value="fifo">FIFO — modal batch masuk pertama keluar dulu</option>
                      </>}
                </select>
              </F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Harga beli (perkiraan)" htmlFor="inv-f-hbeli">
                  {hbeliTampil ? (
                    <InputRupiah id="inv-f-hbeli" value={form.harga_beli} testid="inv-f-hbeli"
                                 onChange={v => setForm(f => ({ ...f, harga_beli: v }))} />
                  ) : (
                    <p data-testid="inv-f-hbeli-tersembunyi"
                       className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
                      Harga beli tidak ditampilkan untuk akun Anda. Nilai yang tersimpan tidak berubah
                      saat Anda menyimpan.
                    </p>
                  )}
                </F>
                <F label="Harga jual" htmlFor="inv-f-hjual">
                  <InputRupiah id="inv-f-hjual" value={form.harga_jual} testid="inv-f-hjual"
                               onChange={v => setForm(f => ({ ...f, harga_jual: v }))} />
                </F>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <F label="Stok minimum" htmlFor="inv-f-minstock" hint="Di bawah ini ditandai merah">
                  <input id="inv-f-minstock" type="number" min="0" value={form.min_stock} data-testid="inv-f-minstock"
                         onChange={e => setForm(f => ({ ...f, min_stock: e.target.value }))} className={inp} />
                </F>
                <F label="Stok maksimum" htmlFor="inv-f-maxstock" hint="Opsional — kosong = tanpa batas atas">
                  <input id="inv-f-maxstock" type="number" min="0" value={form.max_stock ?? ""} data-testid="inv-f-maxstock"
                         onChange={e => setForm(f => ({ ...f, max_stock: e.target.value }))} className={inp} />
                </F>
              </div>
            </div>
            <div className="p-4 border-t border-slate-100 flex items-center justify-end gap-2">
              <button onClick={() => setForm(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
              <button onClick={simpan} disabled={busy} data-testid="inv-prod-submit"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                {busy ? "Menyimpan..." : "Simpan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = "w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200";

function F({ label, hint, htmlFor, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

// ============ Pengaturan Master (poin 10-11-14) ============
function DaftarChip({ label, hint, nilai, setNilai, testid }) {
  const [baru, setBaru] = useState("");
  const tambah = () => {
    const v = baru.trim();
    if (!v) return;
    if (nilai.some(x => x.toLowerCase() === v.toLowerCase())) {
      toast.error(`"${v}" sudah ada di daftar`);
      return;
    }
    setNilai([...nilai, v]);
    setBaru("");
  };
  return (
    <div>
      <label htmlFor={`${testid}-input`} className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <div className="flex gap-2">
        <input id={`${testid}-input`} value={baru} onChange={e => setBaru(e.target.value)}
               onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); tambah(); } }}
               placeholder="ketik lalu Enter" data-testid={`${testid}-input`} className={inp} />
        <button onClick={tambah} data-testid={`${testid}-add`}
                className="h-10 px-3 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-700 text-sm shrink-0">
          Tambah
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2 min-h-[26px]">
        {nilai.map(v => (
          <span key={v} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-xs text-slate-700">
            {v}
            <button onClick={() => setNilai(nilai.filter(x => x !== v))}
                    className="text-slate-400 hover:text-rose-600"><X className="w-3 h-3" /></button>
          </span>
        ))}
        {nilai.length === 0 && <span className="text-[11px] text-slate-400">belum ada — ketikan bebas diizinkan sampai daftar diisi</span>}
      </div>
      {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

// Editor peta kode SKU DIHAPUS (brief owner 12 Sep 2026, lampiran image2 —
// "Di Hapus"). Singkatan yang SUDAH terdaftar tetap hidup: ia jadi pengisi
// otomatis kolom SKU di form produk (`bahanSkuEfektif`), dan `kodeSku` tetap
// dibaca lalu dikirim ulang apa adanya oleh PengaturanModal — jadi menutup
// layar penyuntingnya tidak menghapus satu kode pun. Yang berubah: nilai yang
// belum punya kode TIDAK lagi menolak penyimpanan produk, melainkan memakai
// slug nilainya sendiri (`products.py::_bahan_sku_efektif`).


function PengaturanModal({ opsi, onClose }) {
  // Poin 1 & 6 (brief 23-24 Agu 2026) — DUA daftar brand terpisah menggantikan
  // satu input "Brand terdaftar" lama di layar ini. `brand` legacy TETAP ada
  // di database (dipakai cashback/template import lama) tapi TIDAK diedit
  // dari sini lagi — PUT hanya mengirim `brand_gadget`/`brand_accessories`,
  // jadi field `brand` lama tidak pernah tertimpa dari layar ini (backend:
  // kunci yang tidak dikirim = tidak diubah).
  const [brandGadget, setBrandGadget] = useState(opsi.brand_gadget || []);
  const [brandAccessories, setBrandAccessories] = useState(opsi.brand_accessories || []);
  const [satuan, setSatuan] = useState(opsi.satuan);
  const [katCustom, setKatCustom] = useState(opsi.kategori_custom);
  const [agingHari, setAgingHari] = useState(opsi.aging_hari);
  // Poin 1 brief 31 Agu — peta kode pembangun SKU, disimpan bersama registry
  // lain lewat PUT yang sama (backend: kunci yang tidak dikirim = tidak diubah).
  const [kodeSku, setKodeSku] = useState(opsi.kode_sku || []);
  const [busy, setBusy] = useState(false);

  const simpan = async () => {
    setBusy(true);
    try {
      const r = await api.put("/inventory/master-opsi", {
        brand_gadget: brandGadget, brand_accessories: brandAccessories,
        satuan, kategori_custom: katCustom, aging_hari: Number(agingHari) || 60,
        kode_sku: kodeSku,
      });
      toast.success(r.data.message);
      onClose(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan pengaturan"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => onClose(false)}>
      <div onClick={e => e.stopPropagation()} data-testid="inv-opsi-modal"
           className="bg-white rounded-2xl max-w-lg w-full max-h-[88vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-emerald-600" /> Pengaturan Master Produk
          </h3>
          <button onClick={() => onClose(false)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          <DaftarChip label="Brand Gadget" testid="inv-opsi-brand-gadget"
                      hint="Dipakai kategori Handphone/Tab/Laptop/Wearable/Original Acc/SIM Sticker. Brand boleh ada di kedua daftar (mis. UGREEN bikin TWS dan kabel)."
                      nilai={brandGadget} setNilai={setBrandGadget} />
          <DaftarChip label="Brand Accessories" testid="inv-opsi-brand-accessories"
                      hint="Dipakai HANYA kategori Accessories. Brand boleh ada di kedua daftar."
                      nilai={brandAccessories} setNilai={setBrandAccessories} />
          {/* Blok "Kode SKU per kolom" DIHAPUS atas permintaan owner
              (brief 12 Sep, lampiran image2). Singkatan yang sudah terdaftar
              TETAP dipakai sebagai pengisi otomatis kolom SKU di form produk —
              yang hilang hanya layar untuk menyuntingnya, bukan datanya.
              `kodeSku` masih dibaca & dikirim ulang apa adanya di PUT di bawah,
              jadi menutup layar ini tidak menghapus kode yang sudah ada. */}
          <DaftarChip label="Satuan" testid="inv-opsi-satuan"
                      hint="unit, pcs, box, dus — sesuaikan dengan barang toko."
                      nilai={satuan} setNilai={setSatuan} />
          <DaftarChip label="Kategori custom" testid="inv-opsi-katcustom"
                      hint="Pengelompokan bebas di atas 3 kategori akuntansi (tidak mengubah akun pembukuan)."
                      nilai={katCustom} setNilai={setKatCustom} />
          <F label="Ambang umur stok (hari)" htmlFor="inv-opsi-aging" hint="Barang yang nangkring melebihi ini ditandai merah & bisa difilter 'Stok lama'.">
            <input id="inv-opsi-aging" type="number" min="1" max="3650" value={agingHari} data-testid="inv-opsi-aging"
                   onChange={e => setAgingHari(e.target.value)} className={inp} />
          </F>
        </div>
        <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={() => onClose(false)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
          <button onClick={simpan} disabled={busy} data-testid="inv-opsi-submit"
                  className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
            {busy ? "Menyimpan..." : "Simpan Pengaturan"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ Import produk (poin 11) ============
function ImportModal({ onClose }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hasil, setHasil] = useState(null);

  // E1 (brief owner 22 Sep 2026): DUA template, SATU tombol import — backend
  // mengenali formatnya sendiri dari baris judul (`products.py::import_produk`),
  // jadi staf tidak perlu memilih format saat mengunggah dan tidak bisa salah pilih.
  const unduhTemplate = async (jalur = "import-template", namaBerkas = "template-import-produk.xlsx") => {
    try {
      const token = localStorage.getItem("hris_token");
      const res = await fetch(`${API}/inventory/products/${jalur}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(blob);
      a.download = namaBerkas;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(a.href);
    } catch { toast.error("Gagal mengunduh template"); }
  };

  const kirim = async () => {
    if (!file) return toast.error("Pilih file .xlsx dulu");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/inventory/products/import", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setHasil(r.data);
      toast.success(r.data.message);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal import"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => onClose(!!hasil)}>
      <div onClick={e => e.stopPropagation()} data-testid="inv-import-modal"
           className="bg-white rounded-2xl max-w-lg w-full max-h-[88vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            <Upload className="w-5 h-5 text-emerald-600" /> Import Produk
          </h3>
          <button onClick={() => onClose(!!hasil)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">
          <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-800 space-y-1">
            <p>
              Isi data di salah satu template resmi supaya kolomnya pas — kedua format diterima dan
              dikenali otomatis. Baris bermasalah dilewati dan dilaporkan, sisanya tetap masuk.
            </p>
            <p>
              <b>Format baru</b> (Kategori, Status, bahan Nama &amp; SKU otomatis): produk gadget yang
              sudah ada dikenali dari kombinasinya, jadi impor ulang <b>tidak</b> membuat SKU /2. Untuk
              produk yang sudah ada, sel kosong <b>tidak mengubah</b> apa pun — termasuk Harga Beli.
            </p>
          </div>
          <button onClick={() => unduhTemplate("import-template-bos", "template-import-produk-format-baru.xlsx")}
                  data-testid="inv-import-template-bos"
                  className="w-full h-10 rounded-lg border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-sm font-medium inline-flex items-center justify-center gap-2">
            <Download className="w-4 h-4" /> Unduh Template Excel (format baru)
          </button>
          <button onClick={() => unduhTemplate()} data-testid="inv-import-template"
                  className="w-full h-9 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 text-xs font-medium inline-flex items-center justify-center gap-2">
            <Download className="w-3.5 h-3.5" /> Template format lama (SKU, Nama, Kategori, …)
          </button>
          <F label="File .xlsx yang sudah diisi" htmlFor="inv-import-file">
            <input id="inv-import-file" type="file" accept=".xlsx" data-testid="inv-import-file"
                   onChange={e => setFile(e.target.files?.[0] || null)}
                   className="w-full text-sm file:mr-3 file:h-9 file:px-3 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 file:text-xs" />
          </F>
          {hasil && (
            <div className="border border-slate-200 rounded-lg p-3 space-y-2 text-sm">
              <p className="text-emerald-700 font-medium">{hasil.dibuat} dibuat · {hasil.diperbarui} diperbarui
                {hasil.gagal.length > 0 && <span className="text-rose-700"> · {hasil.gagal.length} gagal</span>}</p>
              {hasil.gagal.length > 0 && (
                <div className="max-h-44 overflow-y-auto space-y-1">
                  {hasil.gagal.map((g, i) => (
                    <p key={i} className="text-xs text-rose-700">Baris {g.baris}: {g.alasan}</p>
                  ))}
                </div>
              )}
              {(hasil.harga_beli_dipertahankan || []).length > 0 && (
                <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2"
                     data-testid="inv-import-hbeli-dipertahankan">
                  <p className="font-medium">
                    {hasil.harga_beli_dipertahankan.length} produk lama dengan Harga Beli kosong — harga beli
                    tersimpan dipertahankan (bukan jadi 0):
                  </p>
                  <p className="font-mono break-words">{hasil.harga_beli_dipertahankan.join(", ")}</p>
                </div>
              )}
              {(hasil.kolom_diabaikan || []).length > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2"
                   data-testid="inv-import-kolom-diabaikan">
                  Kolom tidak dikenal, isinya tidak dibaca: {hasil.kolom_diabaikan.join(", ")}
                </p>
              )}
              {(hasil.harga_beli_diabaikan || []).length > 0 && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2"
                     data-testid="inv-import-hbeli-diabaikan">
                  <p className="font-medium">
                    {hasil.harga_beli_diabaikan.length} harga beli diabaikan (akun Anda tidak melihat modal):
                  </p>
                  <p className="font-mono break-words">{hasil.harga_beli_diabaikan.join(", ")}</p>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={() => onClose(!!hasil)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">
            {hasil ? "Selesai" : "Batal"}
          </button>
          <button onClick={kirim} disabled={busy || !file} data-testid="inv-import-submit"
                  className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
            {busy ? "Memproses..." : "Import Sekarang"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ Bulk Price per brand (poin 12) ============
function BulkHargaModal({ brands, onClose }) {
  const [brand, setBrand] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dimuat, setDimuat] = useState(false);
  const [adaPerubahan, setAdaPerubahan] = useState(false);
  // Batch P (18 Sep 2026) — flag dari server, BUKAN dihitung dari isi baris
  // (baris tingkat 'kelas'/'tidak' sudah tidak punya `harga_avg` sama sekali,
  // lihat `akses_modal.terapkan_ke_produk`).
  const [modalTerlihat, setModalTerlihat] = useState(true);
  const [marginBentuk, setMarginBentuk] = useState("persen");
  const printRef = useRef(null);

  const muat = async (b, qv) => {
    setLoadingRows(true);
    try {
      const r = await api.get("/inventory/products/bulk-harga", {
        params: { brand: b || undefined, q: (qv ?? "").trim() || undefined },
      });
      setRows((r.data.items || []).map(x => ({ ...x, harga_baru: String(Math.round(x.harga_jual || 0)) })));
      setModalTerlihat(!!r.data.modal_terlihat);
      setMarginBentuk(r.data.margin_bentuk || null);
      setDimuat(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat daftar"); }
    finally { setLoadingRows(false); }
  };
  const tampilkan = () => muat(brand, q);

  const ubahHarga = (sku, v) => {
    setRows(rs => rs.map(r => r.sku === sku ? { ...r, harga_baru: v } : r));
  };

  // Cetak (poin 12 Agu #3) — MODAL TIDAK BOLEH TAMPIL. Bukan tabel layar yang
  // disembunyikan CSS: tabel cetak di bawah adalah elemen TERPISAH yang sama
  // sekali tidak pernah diberi field harga_avg/modal, jadi tidak ada cara
  // modal "bocor" ke kertas lewat salah suntik data. `.print-only` (index.css)
  // membuatnya tidak pernah tampak di layar; `cetak()` dari FakturPrint.jsx
  // menyalinnya ke #print-root saat tombol ditekan — mekanisme SAMA yang
  // sudah dipakai nota/faktur (dilarang bikin jalur cetak kedua, lihat
  // CLAUDE.md "faktur keluar 3-4 rangkap").
  const cetakDaftar = () => {
    if (!rows.length) { toast.info("Tidak ada data untuk dicetak — tampilkan daftarnya dulu"); return; }
    cetak(printRef.current);
  };

  const simpan = async () => {
    const diubah = rows.filter(r => Number(r.harga_baru) !== Math.round(r.harga_jual || 0));
    if (!diubah.length) return toast.info("Tidak ada harga yang berubah");
    setBusy(true);
    try {
      const r = await api.post("/inventory/products/bulk-harga", {
        items: diubah.map(x => ({ sku: x.sku, harga_jual: Number(x.harga_baru) })),
      });
      toast.success(r.data.message);
      (r.data.peringatan || []).forEach(p => toast.warning(p, { duration: 8000 }));
      // § LAYAR C / §7 DESAIN-BRIEF-18-SEP.md: untuk tingkat yang tidak
      // melihat modal, `peringatan` SELALU kosong (server tidak pernah
      // membocorkan modal rata-rata lewat teks) — oracle biner ini yang
      // dikirim sebagai gantinya.
      if (r.data.jumlah_di_bawah_modal > 0 && (r.data.peringatan || []).length === 0) {
        toast.warning(
          <span data-testid="inv-bulk-jumlah-bawah">
            {r.data.jumlah_di_bawah_modal} harga baru di bawah modal — minta pemilik/finance memeriksa
          </span>,
          { duration: 8000 },
        );
      }
      setAdaPerubahan(true);
      muat(brand);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan harga"); }
    finally { setBusy(false); }
  };

  const nJadi = rows.filter(r => Number(r.harga_baru) !== Math.round(r.harga_jual || 0)).length;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => onClose(adaPerubahan)}>
      <div onClick={e => e.stopPropagation()} data-testid="inv-bulk-modal"
           className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-lg flex items-center gap-2">
              <Tags className="w-5 h-5 text-amber-600" /> Bulk Harga per Brand
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Ubah harga jual banyak produk sekaligus tanpa membuka satu per satu.</p>
          </div>
          <button onClick={() => onClose(adaPerubahan)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
          {brands.length > 0 ? (
            <select value={brand} onChange={e => setBrand(e.target.value)}
                    data-testid="inv-bulk-brand" aria-label="Filter brand untuk bulk harga"
                    className="h-9 px-3 border border-slate-200 rounded-lg text-sm">
              <option value="">Semua brand</option>
              {brands.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          ) : (
            <p className="text-xs text-slate-500">Belum ada daftar brand — menampilkan semua produk.</p>
          )}
          {/* Pencarian nama produk (poin 12 Agu #3) — sengaja dipicu tombol/Enter,
              bukan live-search per ketikan, supaya tidak menembak API tiap
              huruf untuk layar yang bisa memuat ribuan SKU sekaligus. */}
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={e => setQ(e.target.value)}
                   onKeyDown={e => { if (e.key === "Enter") tampilkan(); }}
                   placeholder="Cari nama/SKU produk" data-testid="inv-bulk-search"
                   aria-label="Cari nama atau SKU produk untuk bulk harga"
                   className="h-9 pl-9 pr-3 border border-slate-200 rounded-lg text-sm w-52" />
          </div>
          <button onClick={tampilkan} data-testid="inv-bulk-load"
                  className="h-9 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium">
            Tampilkan
          </button>
          {dimuat && (
            <button onClick={cetakDaftar} data-testid="inv-bulk-cetak" disabled={!rows.length}
                    title="Cetak daftar harga — kolom modal TIDAK ikut tercetak"
                    className="h-9 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700 inline-flex items-center gap-1.5 disabled:opacity-50">
              <Printer className="w-4 h-4" /> Cetak
            </button>
          )}
          {dimuat && <p className="text-xs text-slate-500">{rows.length} produk</p>}
        </div>
        <div className="flex-1 overflow-y-auto">
          {loadingRows ? (
            <LoadingState loading data-testid="inv-bulk-loading" />
          ) : !dimuat ? (
            <p className="p-10 text-center text-sm text-slate-400">Pilih brand lalu klik "Tampilkan".</p>
          ) : rows.length === 0 ? (
            <p className="p-10 text-center text-sm text-slate-400">Tidak ada produk untuk brand ini.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 sticky top-0">
                <tr className="text-left">
                  <th className="px-4 py-2 font-semibold">Produk</th>
                  <th className="px-4 py-2 font-semibold text-right">Stok</th>
                  {modalTerlihat && <th className="px-4 py-2 font-semibold text-right" data-testid="inv-bulk-th-modal">Modal Rata²</th>}
                  {marginBentuk === "kelas" && <th className="px-4 py-2 font-semibold text-right" data-testid="inv-bulk-th-kelas">Kelas (skrg)</th>}
                  <th className="px-4 py-2 font-semibold text-right">Harga Sekarang</th>
                  <th className="px-4 py-2 font-semibold text-right w-44">Harga Baru</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(r => {
                  const berubah = Number(r.harga_baru) !== Math.round(r.harga_jual || 0);
                  const bawahModal = modalTerlihat && r.harga_avg > 0 && Number(r.harga_baru) < r.harga_avg;
                  return (
                    <tr key={r.sku} className={berubah ? "bg-amber-50/60" : ""}>
                      <td className="px-4 py-2">
                        <p className="text-slate-800">{r.nama}</p>
                        <p className="font-mono text-[10px] text-slate-500">{r.sku}{r.brand ? ` · ${r.brand}` : ""}</p>
                      </td>
                      <td className="px-4 py-2 text-right text-slate-700">{r.stock_qty}</td>
                      {modalTerlihat && (
                        <td className="px-4 py-2 text-right font-mono text-slate-500 text-xs">{r.harga_avg ? idr(r.harga_avg) : "—"}</td>
                      )}
                      {marginBentuk === "kelas" && (
                        <td className="px-4 py-2 text-right">
                          <MarginBadge p={r} bentuk="kelas" suffix="-bulk" />
                        </td>
                      )}
                      <td className="px-4 py-2 text-right font-mono text-slate-700">{idr(r.harga_jual)}</td>
                      <td className="px-4 py-2">
                        <div className={bawahModal ? "ring-2 ring-rose-300 rounded-lg" : ""}>
                          <InputRupiah value={r.harga_baru} testid={`inv-bulk-${r.sku}`}
                                       ariaLabel={`Harga baru untuk ${r.nama}`}
                                       onChange={v => ubahHarga(r.sku, v)} />
                        </div>
                        {bawahModal && <p className="text-[10px] text-rose-600 mt-0.5 text-right">di bawah modal!</p>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ============ Versi cetak (poin 12 Agu #3) ============
            Elemen TERPISAH dari tabel layar di atas — BUKAN tabel yang sama
            dengan kolom modal disembunyikan lewat CSS. Field harga_avg/modal
            sama sekali tidak pernah dirender ke DOM elemen ini, jadi tidak
            ada jalan modal "bocor" ke kertas walau data row berubah. Kelas
            `print-only` (index.css) membuat elemen ini SELALU display:none
            di layar, dan HANYA muncul (display:block) di dalam @media print
            — jadi tidak pernah kelihatan di modal walau tidak sedang dicetak. */}
        <div ref={printRef} className="print-area print-only p-6">
          <style>{"@media print { @page { size: A4; margin: 12mm; } }"}</style>
          <div className="flex items-start justify-between gap-4 border-b-2 border-slate-900 pb-3 mb-3">
            <div>
              <p className="font-display font-bold text-lg">Daftar Harga Produk</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {brand ? `Brand: ${brand}` : "Semua brand"}{q ? ` · Cari: "${q}"` : ""}
              </p>
            </div>
            <p className="text-xs text-slate-500 shrink-0">
              {new Date().toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" })}
            </p>
          </div>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-slate-300 text-[11px] uppercase tracking-wider text-slate-500">
                <th className="text-left py-1.5 pr-2">No</th>
                <th className="text-left py-1.5 pr-2">Produk</th>
                <th className="text-left py-1.5 pr-2">Brand</th>
                <th className="text-right py-1.5 pr-2">Stok</th>
                <th className="text-right py-1.5">Harga Jual</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.sku} className="border-b border-slate-100" data-testid={`inv-bulk-cetak-baris-${r.sku}`}>
                  <td className="py-1.5 pr-2">{i + 1}</td>
                  <td className="py-1.5 pr-2">
                    {r.nama}
                    <span className="block text-[10px] font-mono text-slate-500">{r.sku}</span>
                  </td>
                  <td className="py-1.5 pr-2">{r.brand || "-"}</td>
                  <td className="py-1.5 pr-2 text-right">{r.stock_qty}</td>
                  <td className="py-1.5 text-right font-mono">{idr(Number(r.harga_baru || r.harga_jual || 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t border-slate-100 flex items-center justify-between">
          <p className="text-xs text-slate-500">{nJadi > 0 ? `${nJadi} harga akan diubah` : "Belum ada perubahan"}</p>
          <div className="flex gap-2">
            <button onClick={() => onClose(adaPerubahan)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Tutup</button>
            <button onClick={simpan} disabled={busy || nJadi === 0} data-testid="inv-bulk-submit"
                    className="h-10 px-4 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium disabled:opacity-50">
              {busy ? "Menyimpan..." : `Simpan ${nJadi || ""} Harga`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ Usulan Min. Stok massal (brief owner 14 Agu #8) ============
// Alur DUA LANGKAH sama persis dengan Bulk Harga di atas — muat usulan dulu,
// tinjau/edit di layar, baru simpan HANYA baris yang dicentang. SKU yang
// sudah punya min_stock manual TIDAK dicentang otomatis (`sudah_diisi_manual`)
// supaya tidak tertimpa tanpa disadari; kalau staf sengaja mau menimpanya,
// backend tetap menolak kecuali dicentang eksplisit (lihat `konfirmasi_timpa`).
function UsulanMinStockModal({ brands, onClose }) {
  const [brand, setBrand] = useState("");
  const [q, setQ] = useState("");
  const [periodeHari, setPeriodeHari] = useState(30);
  const [rows, setRows] = useState([]);
  const [penjelasan, setPenjelasan] = useState("");
  const [loadingRows, setLoadingRows] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dimuat, setDimuat] = useState(false);
  const [adaPerubahan, setAdaPerubahan] = useState(false);

  const muat = async () => {
    setLoadingRows(true);
    try {
      const r = await api.get("/inventory/products/usulan-min-stock", {
        params: { brand: brand || undefined, q: q.trim() || undefined, periode_hari: Number(periodeHari) || 30 },
      });
      setPenjelasan(r.data.penjelasan || "");
      setRows((r.data.items || []).map(x => ({
        ...x,
        // Pre-fill nilai yang mau disimpan: usulan kalau ADA, kalau tidak
        // biarkan sama dengan angka sekarang (staf boleh isi manual sendiri).
        nilai_baru: String(x.usulan_min_stock ?? x.min_stock_sekarang ?? 0),
        // Pre-select HANYA yang cukup data DAN belum pernah diisi manual —
        // syarat owner "jangan menimpa yang sudah diisi manual" ditegakkan
        // di sini SEKALIGUS di backend (defense in depth).
        pilih: x.cukup_data && !x.sudah_diisi_manual,
      })));
      setDimuat(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat usulan"); }
    finally { setLoadingRows(false); }
  };

  const ubahBaris = (sku, patch) => {
    setRows(rs => rs.map(r => r.sku === sku ? { ...r, ...patch } : r));
  };

  const dipilih = rows.filter(r => r.pilih);

  const simpan = async () => {
    if (!dipilih.length) return toast.info("Belum ada baris yang dicentang untuk disimpan");
    setBusy(true);
    try {
      const r = await api.post("/inventory/products/bulk-min-stock", {
        items: dipilih.map(x => ({
          sku: x.sku, min_stock: Number(x.nilai_baru) || 0,
          konfirmasi_timpa: !!x.sudah_diisi_manual,
        })),
      });
      toast.success(r.data.message);
      if ((r.data.dilewati_manual || []).length) {
        toast.warning(`Dilewati (sudah manual, coba lagi kalau memang mau menimpa): ${r.data.dilewati_manual.join(", ")}`,
          { duration: 8000 });
      }
      setAdaPerubahan(true);
      muat();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan min_stock"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => onClose(adaPerubahan)}>
      <div onClick={e => e.stopPropagation()} data-testid="inv-usulan-minstock-modal"
           className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-sky-600" /> Usulan Stok Minimum Otomatis
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Usulan = rata-rata penjualan harian x lead time supplier x 1,5. Tinjau angkanya dulu — belum ada yang
              tersimpan sampai tombol Simpan ditekan.
            </p>
          </div>
          <button onClick={() => onClose(adaPerubahan)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
          {brands.length > 0 ? (
            <select value={brand} onChange={e => setBrand(e.target.value)}
                    data-testid="inv-usulan-brand" aria-label="Filter brand untuk usulan min stok"
                    className="h-9 px-3 border border-slate-200 rounded-lg text-sm">
              <option value="">Semua brand</option>
              {brands.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          ) : (
            <p className="text-xs text-slate-500">Belum ada daftar brand — menampilkan semua produk.</p>
          )}
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={e => setQ(e.target.value)}
                   onKeyDown={e => { if (e.key === "Enter") muat(); }}
                   placeholder="Cari nama/SKU produk" data-testid="inv-usulan-search"
                   aria-label="Cari nama atau SKU produk untuk usulan min stok"
                   className="h-9 pl-9 pr-3 border border-slate-200 rounded-lg text-sm w-52" />
          </div>
          <div className="flex items-center gap-1.5">
            <label htmlFor="inv-usulan-periode" className="text-xs text-slate-500">Periode rata-rata (hari)</label>
            <input id="inv-usulan-periode" type="number" min="7" max="180" value={periodeHari}
                   onChange={e => setPeriodeHari(e.target.value)} data-testid="inv-usulan-periode"
                   className="h-9 w-16 px-2 border border-slate-200 rounded-lg text-sm text-center" />
          </div>
          <button onClick={muat} data-testid="inv-usulan-load"
                  className="h-9 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium">
            Tampilkan
          </button>
          {dimuat && <p className="text-xs text-slate-500">{rows.length} produk · {dipilih.length} dicentang</p>}
        </div>
        {penjelasan && (
          <p className="px-5 pt-2 text-[11px] text-slate-500">{penjelasan}</p>
        )}
        <div className="flex-1 overflow-y-auto">
          {loadingRows ? (
            <LoadingState loading data-testid="inv-usulan-loading" />
          ) : !dimuat ? (
            <p className="p-10 text-center text-sm text-slate-400">Atur filter lalu klik "Tampilkan".</p>
          ) : rows.length === 0 ? (
            <p className="p-10 text-center text-sm text-slate-400">Tidak ada produk untuk filter ini.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 sticky top-0">
                <tr className="text-left">
                  <th className="px-3 py-2 w-8"></th>
                  <th className="px-4 py-2 font-semibold">Produk</th>
                  <th className="px-4 py-2 font-semibold text-right">Min. Sekarang</th>
                  <th className="px-4 py-2 font-semibold text-right">Rata²/hari</th>
                  <th className="px-4 py-2 font-semibold">Lead Time (Supplier)</th>
                  <th className="px-4 py-2 font-semibold text-right w-32">Min. Stok Baru</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(r => (
                  <tr key={r.sku} data-testid={`inv-usulan-row-${r.sku}`}
                      className={r.pilih ? "bg-sky-50/60" : ""}>
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={r.pilih} data-testid={`inv-usulan-pilih-${r.sku}`}
                             aria-label={`Pilih ${r.sku} untuk disimpan`}
                             onChange={e => ubahBaris(r.sku, { pilih: e.target.checked })}
                             className="w-4 h-4 rounded border-slate-300" />
                    </td>
                    <td className="px-4 py-2">
                      <p className="text-slate-800">{r.nama}</p>
                      <p className="font-mono text-[10px] text-slate-500">{r.sku}{r.brand ? ` · ${r.brand}` : ""}</p>
                      {r.sudah_diisi_manual && (
                        <p className="text-[10px] text-amber-600 mt-0.5">sudah diisi manual — centang berarti menimpa</p>
                      )}
                      {!r.cukup_data && (
                        <p className="text-[10px] text-slate-500 mt-0.5" data-testid={`inv-usulan-catatan-${r.sku}`}>{r.catatan}</p>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right text-slate-700">{r.min_stock_sekarang}</td>
                    <td className="px-4 py-2 text-right text-slate-500 text-xs">{r.rata_harian ?? "—"}</td>
                    <td className="px-4 py-2 text-xs text-slate-600">
                      {r.lead_time_hari != null ? `${r.lead_time_hari} hari (${r.supplier_nama || "-"})` : "—"}
                    </td>
                    <td className="px-4 py-2">
                      <input type="number" min="0" value={r.nilai_baru} data-testid={`inv-usulan-nilai-${r.sku}`}
                             aria-label={`Min stok baru untuk ${r.nama}`}
                             onChange={e => ubahBaris(r.sku, { nilai_baru: e.target.value })}
                             className="w-full h-9 px-2 border border-slate-200 rounded-lg text-sm text-right font-mono" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="p-4 border-t border-slate-100 flex items-center justify-between">
          <p className="text-xs text-slate-500">{dipilih.length > 0 ? `${dipilih.length} produk akan disimpan` : "Belum ada yang dicentang"}</p>
          <div className="flex gap-2">
            <button onClick={() => onClose(adaPerubahan)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Tutup</button>
            <button onClick={simpan} disabled={busy || dipilih.length === 0} data-testid="inv-usulan-submit"
                    className="h-10 px-4 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium disabled:opacity-50">
              {busy ? "Menyimpan..." : `Simpan ${dipilih.length || ""} Min. Stok`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* P5 (brief 4 Sep): sebaran stok satu produk per gudang. Membaca endpoint
   detail yang SUDAH ada (`GET /inventory/products/{sku}` -> `per_gudang[]`,
   qty dari kartu stok, sudah disaring lingkup cabang). Tidak menghitung apa pun
   di klien — kalau angkanya beda dengan Posisi Stok, yang salah bukan layar ini. */
function RincianGudangModal({ produk, gudangTerpilih, onClose }) {
  const [data, setData] = useState(null);
  const [gagal, setGagal] = useState(null);
  useEffect(() => {
    let hidup = true;
    api.get(`/inventory/products/${encodeURIComponent(produk.sku)}`)
      .then(r => { if (hidup) setData(r.data); })
      .catch(e => { if (hidup) setGagal(e?.response?.data?.detail || "Gagal memuat rincian"); });
    return () => { hidup = false; };
  }, [produk.sku]);
  const baris = data?.per_gudang || [];
  const total = baris.reduce((s, g) => s + (Number(g.qty) || 0), 0);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="inv-rincian-gudang-modal"
           className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-display font-bold text-lg">Sebaran Stok per Gudang</h3>
            <p className="text-xs text-slate-500 font-mono">{produk.sku} · {produk.nama}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">
          {gagal && <p className="text-sm text-rose-700">{gagal}</p>}
          {!gagal && !data && <p className="text-sm text-slate-500">Memuat…</p>}
          {data && baris.length === 0 && (
            <p className="text-sm text-slate-500">Tidak ada stok di gudang mana pun (yang boleh kamu lihat).</p>
          )}
          {baris.length > 0 && (
            <table className="w-full text-sm">
              <thead><tr className="text-xs text-slate-500 uppercase">
                <th className="text-left py-2">Gudang</th><th className="text-right py-2">Qty</th>
              </tr></thead>
              <tbody>
                {baris.map(g => (
                  <tr key={g.warehouse_id} data-testid={`inv-rincian-gudang-${g.warehouse_id}`}
                      className={`border-t border-slate-100 ${g.warehouse_id === gudangTerpilih ? "bg-emerald-50/60" : ""}`}>
                    <td className="py-2">
                      {g.warehouse_nama}
                      {g.warehouse_id === gudangTerpilih && <span className="ml-1 text-[10px] text-emerald-700">(filter aktif)</span>}
                    </td>
                    <td className={`py-2 text-right font-mono ${g.qty < 0 ? "text-rose-700 font-semibold" : ""}`}>{g.qty}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-slate-200 font-semibold">
                  <td className="py-2">Total</td>
                  <td className="py-2 text-right font-mono" data-testid="inv-rincian-total">{total}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function LabelModal({ skus, onClose, onCetak }) {
  const [ukuran, setUkuran] = useState("sedang");
  const [perItem, setPerItem] = useState(1);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="inv-label-modal"
           className="bg-white rounded-2xl max-w-sm w-full shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            <Printer className="w-5 h-5 text-emerald-600" /> Cetak Label
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-slate-600">{skus.length} produk dipilih</p>
          <F label="Ukuran label" htmlFor="inv-label-ukuran">
            <select id="inv-label-ukuran" value={ukuran} onChange={e => setUkuran(e.target.value)}
                    data-testid="inv-label-ukuran" className={inp}>
              <option value="kecil">Kecil (38 mm)</option>
              <option value="sedang">Sedang (50 mm)</option>
              <option value="besar">Besar (70 mm)</option>
            </select>
          </F>
          <F label="Lembar per produk" htmlFor="inv-label-jumlah" hint="Maksimal 200 label sekali cetak">
            <input id="inv-label-jumlah" type="number" min="1" max="50" value={perItem} data-testid="inv-label-jumlah"
                   onChange={e => setPerItem(Number(e.target.value))} className={inp} />
          </F>
        </div>
        <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
          <button onClick={() => onCetak(skus, ukuran, perItem)} data-testid="inv-label-cetak"
                  className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
            Unduh PDF
          </button>
        </div>
      </div>
    </div>
  );
}
